import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sidebar from './components/Sidebar';
import MetricCard from './components/MetricCard';
import { AllocationChart, DividendChart } from './components/Charts';
import PortfolioTable from './components/PortfolioTable';
import AssetDetail from './components/AssetDetail';
import Login from './components/Login';
import DividendEvents from './components/DividendEvents';
import FutureDividends from './components/FutureDividends';
import DividendPayoutConfirmations, { type DividendPayoutDraft } from './components/DividendPayoutConfirmations';
import DevGlobalDividendCalendarRefresh, { canShowGlobalDividendCalendarRefresh } from './components/DevGlobalDividendCalendarRefresh';
import OnboardingWizard from './components/OnboardingWizard';
import PortfolioUploadModal from './components/PortfolioUploadModal';
import {
    Asset,
    AssetOverrides,
    DividendEvent,
    FireConfig,
    Portfolio,
    SupportedCurrency,
    Transaction,
    UserProfile,
    ViewState,
} from './types';
import {
    Activity,
    ArrowUp,
    Bell,
    CalendarRange,
    Check,
    ChevronLeft,
    ChevronRight,
    ChevronDown,
    CircleHelp,
    Crown,
    Download,
    Flame,
    Globe,
    HandCoins,
    LineChart,
    Loader2,
    LogOut,
    Menu,
    Plus,
    Pencil,
    Settings,
    Moon,
    Search,
    Sparkles,
    Trash2,
    User,
    SunMedium,
    Wallet,
    X,
} from 'lucide-react';
import { auth, db, logout } from './firebase';
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    query,
    serverTimestamp,
    where,
    writeBatch,
    setDoc,
} from 'firebase/firestore';
import { convertCurrency, getCurrencySymbol } from './utils/currency';
import TransactionModal, { type TransactionModalDraft } from './components/TransactionModal';
import { MarketQuote } from './services/marketDataService';
import { getQuoteByProviderKey, getLegacyQuote, getQuoteForInstrument } from './services/quoteService';
import { useQuoteWatchlist } from './hooks/useQuoteWatchlist';
import { removeQuoteWatchlistSymbols, upsertQuoteWatchlistSymbols, type QuoteWatchlistItemInput } from './services/quoteWatchlistService';
import FireSnapshot from './components/FireSnapshot';
import PensionDashboard from './components/PensionDashboard';
import { buildCanonicalNetWorth } from './utils/netWorth';
import FireJourneyChart from './components/FireJourneyChart';
import AnalystSnapshot from './components/ai/AnalystSnapshot';
import { getJourneyStart, getSnapshotsWithAge, requiredNetWorthAtAge } from './utils/fireJourney';
import { computeFireWealthBreakdown, safeDivide } from './utils/fireMetrics';
import { loadPortfoliosWithTransactions, syncPortfoliosToFirestore } from './services/portfolioFirestoreService';
import { getDashboardDisplayName } from './utils/displayName';
import { partitionPendingPayoutsByHeldSymbols } from './utils/dividendPayouts';
import { info, warn, setLoggingEnabled } from './utils/logger';
import { buildPortfolioAnalyticsFromTransactions } from './utils/portfolioAnalytics';
import { fetchAnalystSnapshot } from './services/aiAnalystService';
import { analyzePortfolio } from './services/geminiService';
import { AnalystSnapshotResponse } from './types/aiAnalyst';
import { buildQuoteProviderRequest } from './utils/quoteRequest';
import { isPenceSterling } from './utils/quoteNormalization';
import { getQuoteAliasKeysFromRequest, resolveLiveQuoteForAsset } from './utils/quoteLookup';
import { CachedQuoteRequest, queueQuoteRequestFromProviderKey } from './utils/cachedQuoteLoading';
import { fetchCachedQuote as fetchCachedQuoteByInstrumentKey } from './services/quoteCacheService';
import { calculatePassiveIncomeMetrics, calculatePortfolioMetrics as calculatePortfolioIncomeMetrics } from './utils/passiveIncome';
import { buildSelectedPortfolioHeaderCards } from './utils/portfolioHeaderCards';
import { buildDashboardDividendIncomeKpi } from './utils/dashboardDividendIncome';
import { calculateFireSetupCompletion, type FireSetupActionTarget } from './utils/fireSetupCompletion';
import { loadGlobalDividendEventsForSymbols, type GlobalDividendCalendarEvent } from './services/globalDividendCalendarService';
import { loadProjectedDividendEventsForSymbols, type ProjectedDividendEvent } from './services/dividendProjectionService';
import { buildUserDividendForecast, type UserDividendForecastResult } from './utils/userDividendForecast';

const QUOTE_CURRENCY_EPSILON = 1e-6;

const devGlobalDividendCalendarRefreshEndpointBase =
    import.meta.env?.VITE_DEV_REFRESH_GLOBAL_DIVIDEND_CALENDAR_URL ||
    import.meta.env?.VITE_DIVIDEND_ENDPOINT_BASE_URL ||
    import.meta.env?.VITE_MARKET_DATA_ENDPOINT_BASE_URL ||
    import.meta.env?.VITE_QUOTE_ENDPOINT_BASE_URL ||
    '';

const isDevelopment = () => {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') return true;
    if (typeof window === 'undefined') return false;
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);
};

const isDebugQuotesEnabled = () => {
    try {
        return localStorage.getItem('debugQuotes') === '1';
    } catch {
        return false;
    }
};

const debugPortfolioQuotes = (message: string, details: Record<string, unknown>) => {
    if (!isDebugQuotesEnabled()) return;
    console.debug(message, details);
};

const normalizeDividendQuerySymbol = (value: unknown) => String(value || '').trim().toUpperCase();

const buildDividendQuerySymbolsFromHoldings = (holdings: Array<{ symbol?: string | null; aliases?: string[] }>) => Array.from(new Set(
    holdings.flatMap((holding) => [holding.symbol, ...(holding.aliases || [])])
        .map(normalizeDividendQuerySymbol)
        .filter(Boolean)
)).sort();

const normalizeQuoteCurrencyToken = (value?: string | null) => (value || '').trim().toUpperCase();

const isUkQuoteExchange = (exchange?: string | null) => {
    const ex = normalizeQuoteCurrencyToken(exchange);
    return ex.includes('LSE') || ex.includes('XLON') || ex.includes('LON') || ex.includes('LONDON');
};

const resolveEffectiveQuoteCurrency = (opts: {
    reportedCurrency?: string | null;
    exchange?: string | null;
    price?: number | null;
    referencePrice?: number | null;
}) => {
    const reported = normalizeQuoteCurrencyToken(opts.reportedCurrency);
    if (reported !== 'GBX') return reported || null;

    const price = Number(opts.price);
    const reference = Number(opts.referencePrice);
    if (!Number.isFinite(price) || price <= 0) return 'GBX';
    if (!Number.isFinite(reference) || reference <= 0) return 'GBX';
    if (!isUkQuoteExchange(opts.exchange)) return 'GBX';

    const asGbpFromPence = price * 0.01;
    const relErrAssumeGbx = Math.abs(asGbpFromPence - reference) / Math.max(reference, QUOTE_CURRENCY_EPSILON);
    const relErrAssumeGbp = Math.abs(price - reference) / Math.max(reference, QUOTE_CURRENCY_EPSILON);

    if (relErrAssumeGbp + 0.05 < relErrAssumeGbx) return 'GBP';
    return 'GBX';
};

const cleanUndefinedDeep = <T,>(value: T): T => {
    if (value === null || value === undefined) return value;
    if (value instanceof Date) return value;
    if (Array.isArray(value)) return value.map((item) => cleanUndefinedDeep(item)) as unknown as T;
    if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, val]) => {
            if (val !== undefined) {
                acc[key] = cleanUndefinedDeep(val);
            }
            return acc;
        }, {});
        return entries as T;
    }
    return value;
};


const isFirestoreInternalAssertionError = (error: unknown): boolean => {
    if (!(error instanceof Error)) return false;
    const message = String(error.message || '');
    return message.includes('FIRESTORE') && message.includes('INTERNAL ASSERTION FAILED');
};


const normalizeTransaction = (raw: Partial<Transaction>): Transaction | null => {
    const symbol = String(raw.symbol || '')
        .trim()
        .toUpperCase();
    const actionRaw = String(raw.action || '').trim();
    const action = actionRaw ? `${actionRaw.charAt(0).toUpperCase()}${actionRaw.slice(1).toLowerCase()}` : '';
    const date = String(raw.date || '').trim();
    if (!symbol || !action || !date) return null;

    return {
        id: String(raw.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
        date,
        action: action as Transaction['action'],
        symbol,
        name: String(raw.name || symbol).trim() || symbol,
        isin: raw.isin ? String(raw.isin).trim() : undefined,
        exchange: raw.exchange ? String(raw.exchange).trim() : undefined,
        dataProviderId: raw.dataProviderId ? String(raw.dataProviderId).trim() : undefined,
        instrumentId: raw.instrumentId ? String(raw.instrumentId).trim() : undefined,
        unresolved: Boolean(raw.unresolved),
        unresolvedAmount: Boolean(raw.unresolvedAmount),
        displaySymbol: raw.displaySymbol ? String(raw.displaySymbol).trim().toUpperCase() : undefined,
        quantity: Number(raw.quantity) || 0,
        price: Number(raw.price) || 0,
        priceCurrency: String(raw.priceCurrency || raw.currency || 'GBP').trim().toUpperCase(),
        originalInputPrice: Number.isFinite(Number(raw.originalInputPrice)) ? Number(raw.originalInputPrice) : undefined,
        originalInputPriceUnit: raw.originalInputPriceUnit ? String(raw.originalInputPriceUnit).trim().toUpperCase() : undefined,
        costCurrency: raw.costCurrency ? String(raw.costCurrency).trim().toUpperCase() : undefined,
        marketQuoteUnit: raw.marketQuoteUnit ? String(raw.marketQuoteUnit).trim().toUpperCase() : undefined,
        amount: Number(raw.amount) || 0,
        fees: Number(raw.fees) || 0,
        tax: Number(raw.tax) || 0,
        currency: String(raw.currency || raw.priceCurrency || 'GBP').trim().toUpperCase(),
        source:
            raw.source === 'manual' || raw.source === 'csv' || raw.source === 'auto' || raw.source === 'snapshot'
                ? raw.source
                : undefined,
        provider: raw.provider ? String(raw.provider).trim().toLowerCase() : undefined,
        sourceLabel: raw.sourceLabel ? String(raw.sourceLabel).trim() : undefined,
        originalPricePerShare: Number.isFinite(Number(raw.originalPricePerShare)) ? Number(raw.originalPricePerShare) : undefined,
        originalPriceCurrency: raw.originalPriceCurrency ? String(raw.originalPriceCurrency).trim().toUpperCase() : undefined,
        originalExchangeRate: Number.isFinite(Number(raw.originalExchangeRate)) ? Number(raw.originalExchangeRate) : undefined,
        shares: Number.isFinite(Number(raw.shares)) ? Number(raw.shares) : undefined,
        pricePerShare: Number.isFinite(Number(raw.pricePerShare)) ? Number(raw.pricePerShare) : undefined,
        total: Number.isFinite(Number(raw.total)) ? Number(raw.total) : undefined,
        totalCurrency: raw.totalCurrency ? String(raw.totalCurrency).trim().toUpperCase() : undefined,
        fxRate: Number.isFinite(Number(raw.fxRate)) ? Number(raw.fxRate) : undefined,
        deletedAt: raw.deletedAt ? String(raw.deletedAt) : undefined,
        deletedBy: raw.deletedBy ? String(raw.deletedBy) : undefined,
    };
};

const normalizeTransactions = (rawTransactions: unknown): Transaction[] => {
    const normalized: Transaction[] = [];
    const candidates = Array.isArray(rawTransactions)
        ? rawTransactions
        : rawTransactions && typeof rawTransactions === 'object'
          ? Object.values(rawTransactions as Record<string, unknown>)
          : [];

    candidates.forEach((candidate) => {
        if (!candidate || typeof candidate !== 'object') return;
        const tx = normalizeTransaction(candidate as Partial<Transaction>);
        if (tx) normalized.push(tx);

        Object.values(candidate as Record<string, unknown>).forEach((nested) => {
            if (!nested || typeof nested !== 'object') return;
            const nestedTx = normalizeTransaction(nested as Partial<Transaction>);
            if (nestedTx) normalized.push(nestedTx);
        });
    });

    const seen = new Set<string>();
    return normalized.filter((tx) => {
        if (seen.has(tx.id)) return false;
        seen.add(tx.id);
        return true;
    });
};

const normalizePortfolio = (raw: unknown): Portfolio | null => {
    if (!raw || typeof raw !== 'object') return null;
    const candidate = raw as Partial<Portfolio> & Record<string, unknown>;
    const id = String(candidate.id || '').trim();
    if (!id) return null;
    return {
        id,
        name: String(candidate.name || 'Portfolio').trim() || 'Portfolio',
        transactions: normalizeTransactions(candidate.transactions),
        created: Number(candidate.created) || Date.now(),
    };
};

const getStoredSettings = () => {
    try {
        return JSON.parse(localStorage.getItem('frostball_settings') || '{}');
    } catch {
        return {};
    }
};

const ONBOARDING_STEPS = 8;

const INITIAL_PORTFOLIO: Portfolio = {
    id: 'default',
    name: 'Main Portfolio',
    transactions: [],
    created: Date.now(),
};

const DEFAULT_FIRE_CONFIG: FireConfig = {
    currentAge: 32,
    retirementAge: 60,
    lifeExpectancy: 90,
    annualExpenses: 42000,
    retirementExpenses: 30000,
    investmentReturnRate: 7,
    inflationRate: 2.5,
    incomeGrowthRate: 2.5,
    withdrawalRate: 4,
    fireStyle: 'Traditional',
    riskTolerance: 'Medium',
    country: undefined,
    lifestyleGoals: 'Geo-arbitrage move, slow travel, and passion projects.',
    enableConsoleLogs: true,
    incomeStreams: [],
    manualAssets: [],
    liabilities: [],
    pensions: [],
    snapshots: [],
    onboarding: {
        isComplete: false,
        completedAt: null,
    },
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;
const DAYS_PER_MONTH = 30.44;
const DAYS_PER_YEAR = 365.25;

const startOfUtcDay = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));

const toIsoDate = (date: Date) => date.toISOString().slice(0, 10);

const formatJourneyDuration = (
    snapshots: FireConfig['snapshots'],
    lastUpdated?: number,
    onboardingCompletedAt?: number | null
) => {
    if (!snapshots?.length) {
        const fallbackTimestamp =
            typeof onboardingCompletedAt === 'number' && Number.isFinite(onboardingCompletedAt)
                ? onboardingCompletedAt
                : typeof lastUpdated === 'number' && Number.isFinite(lastUpdated)
                  ? lastUpdated
                  : null;
        if (fallbackTimestamp !== null) {
            const days = Math.max(0, Math.floor((Date.now() - fallbackTimestamp) / MS_PER_DAY));
            if (days <= 0) return '0d';
            const years = Math.floor(days / DAYS_PER_YEAR);
            if (years >= 1) return `${years}y`;
            const months = Math.floor(days / DAYS_PER_MONTH);
            if (months >= 1) return `${months}m`;
            return `${days}d`;
        }
        return '0d';
    }

    const earliestTimestamp = snapshots.reduce<number | null>((earliest, snapshot) => {
        const timestamp = new Date(snapshot.date).getTime();
        if (Number.isNaN(timestamp)) return earliest;
        if (earliest === null || timestamp < earliest) return timestamp;
        return earliest;
    }, null);

    if (earliestTimestamp === null) return '0d';

    const days = Math.max(0, Math.floor((Date.now() - earliestTimestamp) / MS_PER_DAY));
    const years = Math.floor(days / DAYS_PER_YEAR);

    if (years >= 1) return `${years}y`;

    const months = Math.floor(days / DAYS_PER_MONTH);

    if (months >= 1) return `${months}m`;

    return `${days}d`;
};

type FireSnapshotLite = {
    netWorth: number;
    fireProgress: number;
    timestamp: number;
};

const coerceNumber = (value: unknown, fallback: number) => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeIncomeStreams = (
    streams: FireConfig['incomeStreams'] | undefined,
    defaults: { currentAge: number; retirementAge: number }
) =>
    (streams || [])
        .map((stream) => {
            if (!stream) return null;
            const startAge = coerceNumber(stream.startAge, defaults.currentAge);
            const endAge = coerceNumber(stream.endAge, defaults.retirementAge);
            const amount = coerceNumber(stream.amount, 0);
            return {
                ...stream,
                amount,
                startAge,
                endAge: Math.max(endAge, startAge),
            };
        })
        .filter(Boolean) as FireConfig['incomeStreams'];

const normalizeAnnualExpense = (value: unknown, fallback: number) => {
    const numeric = coerceNumber(value, fallback);
    if (numeric > 0 && numeric < 2000) {
        return numeric * 12;
    }
    return numeric;
};

const normalizeLiabilities = (items: FireConfig['liabilities'] | undefined) =>
    (items || [])
        .map((liability) => {
            if (!liability) return null;
            return {
                ...liability,
                balance: coerceNumber(liability.balance, 0),
                interestRate: coerceNumber(liability.interestRate, 0),
                monthlyPayment: coerceNumber(liability.monthlyPayment, 0),
            };
        })
        .filter(Boolean) as FireConfig['liabilities'];

const mergeFireConfig = (incoming: Partial<FireConfig> | null | undefined): FireConfig => {
    const base = incoming || {};
    const currentAge = coerceNumber(base?.currentAge, DEFAULT_FIRE_CONFIG.currentAge);
    const retirementAge = coerceNumber(base?.retirementAge, DEFAULT_FIRE_CONFIG.retirementAge);
    return {
        ...DEFAULT_FIRE_CONFIG,
        ...base,
        currentAge,
        retirementAge,
        annualExpenses: normalizeAnnualExpense(base?.annualExpenses, DEFAULT_FIRE_CONFIG.annualExpenses),
        retirementExpenses: normalizeAnnualExpense(
            base?.retirementExpenses || base?.annualExpenses,
            DEFAULT_FIRE_CONFIG.retirementExpenses
        ),
        incomeGrowthRate: coerceNumber(
            base?.incomeGrowthRate ?? base?.inflationRate,
            DEFAULT_FIRE_CONFIG.incomeGrowthRate
        ),
        incomeStreams: normalizeIncomeStreams(base?.incomeStreams, { currentAge, retirementAge }),
        manualAssets: base?.manualAssets || [],
        liabilities: normalizeLiabilities(base?.liabilities),
        pensions: base?.pensions || [],
        riskTolerance: base?.riskTolerance || DEFAULT_FIRE_CONFIG.riskTolerance,
        country: base?.country || DEFAULT_FIRE_CONFIG.country,
        enableConsoleLogs: base?.enableConsoleLogs ?? DEFAULT_FIRE_CONFIG.enableConsoleLogs,
        snapshots: base?.snapshots || [],
        onboarding: {
            isComplete: base?.onboarding?.isComplete ?? DEFAULT_FIRE_CONFIG.onboarding?.isComplete ?? false,
            completedAt: base?.onboarding?.completedAt ?? DEFAULT_FIRE_CONFIG.onboarding?.completedAt ?? null,
        },
    };
};

type ThemeMode = 'light' | 'dark';

type AccountPanel = 'private' | 'public' | 'fire-setup' | 'developer' | 'security' | 'notifications' | 'delete';

type GeneralAccountSettings = {
    displayName: string;
    fullName: string;
    email: string;
    username: string;
    blogName: string;
    socialLinks: Array<{ label: string; url: string }>;
    receiveEmailNotifications: boolean;
    receiveBlogDigest: boolean;
};

const buildDefaultAccountSettings = (profile: UserProfile | null): GeneralAccountSettings => ({
    displayName: '',
    fullName: profile?.name || profile?.displayName || '',
    email: profile?.email || '',
    username: '',
    blogName: '',
    socialLinks: Array.from({ length: 3 }, () => ({ label: '', url: '' })),
    receiveEmailNotifications: true,
    receiveBlogDigest: true,
});

const normalizeAccountSettings = (raw: unknown, profile: UserProfile | null): GeneralAccountSettings => {
    const base = buildDefaultAccountSettings(profile);
    if (!raw || typeof raw !== 'object') return base;
    const value = raw as Partial<GeneralAccountSettings>;
    const socialLinks = Array.isArray(value.socialLinks)
        ? value.socialLinks.slice(0, 3).map((link) => ({
              label: typeof link?.label === 'string' ? link.label : '',
              url: typeof link?.url === 'string' ? link.url : '',
          }))
        : base.socialLinks;
    while (socialLinks.length < 3) {
        socialLinks.push({ label: '', url: '' });
    }

    return {
        displayName: typeof value.displayName === 'string' ? value.displayName : base.displayName,
        fullName: typeof value.fullName === 'string' ? value.fullName : base.fullName,
        email: typeof value.email === 'string' ? value.email : base.email,
        username: typeof value.username === 'string' ? value.username : base.username,
        blogName: typeof value.blogName === 'string' ? value.blogName : base.blogName,
        socialLinks,
        receiveEmailNotifications:
            typeof value.receiveEmailNotifications === 'boolean' ? value.receiveEmailNotifications : base.receiveEmailNotifications,
        receiveBlogDigest: typeof value.receiveBlogDigest === 'boolean' ? value.receiveBlogDigest : base.receiveBlogDigest,
    };
};

const isUkExchange = (exchange?: string) => {
    const value = (exchange || '').toUpperCase();
    return value.includes('LSE') || value.includes('XLON') || value.includes('LON') || value.includes('LONDON');
};

const inferQuoteCurrency = (txCurrency?: string, exchange?: string) => {
    const currency = (txCurrency || '').toUpperCase();
    if (currency) return currency;
    if (isUkExchange(exchange)) return 'GBP';
    return 'USD';
};

const inferQuoteScaleToBase = (quote: string, base: string) => {
    const q = (quote || '').toUpperCase();
    const b = (base || '').toUpperCase();
    if (isPenceSterling({ currency: q }) && b === 'GBP') return 0.01;
    return 1;
};

const normalizeQuoteKey = (value?: string | null) => (value ? value.toUpperCase() : undefined);

interface PendingDividendPayoutRecord extends DividendPayoutDraft {
    payDate: string;
    status: 'pending' | 'confirmed';
}

function App() {
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isGuest, setIsGuest] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [view, setView] = useState<ViewState>('dashboard');
    const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
    const [selectedCurrency, setSelectedCurrency] = useState<SupportedCurrency>(() => {
        const settings = getStoredSettings();
        return settings.currency || 'GBP';
    });
    const [holdingsCurrencyMode, setHoldingsCurrencyMode] = useState<'holding' | 'preferred'>('holding');
    const [currentPortfolioId, setCurrentPortfolioId] = useState<string>(() => {
        const settings = getStoredSettings();
        return settings.lastPortfolioId || '';
    });
    const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [showPortfolioMenu, setShowPortfolioMenu] = useState(false);
    const [showCurrencyMenu, setShowCurrencyMenu] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
    const [showMyAccountPanel, setShowMyAccountPanel] = useState(false);
    const [accountPanel, setAccountPanel] = useState<AccountPanel>('private');
    const [accountSettings, setAccountSettings] = useState<GeneralAccountSettings>(() =>
        normalizeAccountSettings(getStoredSettings().accountSettings, null)
    );
    const [unreadSettingsNotifications, setUnreadSettingsNotifications] = useState<number>(() => {
        const value = Number(localStorage.getItem('frostball_settings_unread_notifications') || '1');
        return Number.isFinite(value) && value >= 0 ? value : 1;
    });
    const [showTransactionModal, setShowTransactionModal] = useState(false);
    const [transactionModalDraft, setTransactionModalDraft] = useState<TransactionModalDraft | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [holdingDeleteCandidate, setHoldingDeleteCandidate] = useState<Asset | null>(null);
    const [globalDividendEvents, setGlobalDividendEvents] = useState<GlobalDividendCalendarEvent[]>([]);
    const [projectedDividendEvents, setProjectedDividendEvents] = useState<ProjectedDividendEvent[]>([]);
    const [isLoadingGlobalDividendEvents, setIsLoadingGlobalDividendEvents] = useState(false);
    const [globalDividendEventsError, setGlobalDividendEventsError] = useState<string | null>(null);
    const [liveQuotes, setLiveQuotes] = useState<Record<string, MarketQuote>>({});
    const [holdingsPageSize, setHoldingsPageSize] = useState<number>(25);
    const [holdingsPage, setHoldingsPage] = useState<number>(0);
    const [analystSnapshot, setAnalystSnapshot] = useState<AnalystSnapshotResponse | null>(null);
    const [portfolioInsight, setPortfolioInsight] = useState<string | null>(null);
    const [portfolioInsightError, setPortfolioInsightError] = useState<string | null>(null);
    const [isLoadingAnalystSnapshot, setIsLoadingAnalystSnapshot] = useState(false);
    const [analystSnapshotError, setAnalystSnapshotError] = useState<string | null>(null);
    const [dividendHorizonDays, setDividendHorizonDays] = useState(365);
    const [futureDividendHorizonMonths, setFutureDividendHorizonMonths] = useState(12);
    const [pendingDividendPayouts, setPendingDividendPayouts] = useState<PendingDividendPayoutRecord[]>([]);
    const [fireConfig, setFireConfig] = useState<FireConfig>(() => mergeFireConfig(null));
    const [fireConfigLoaded, setFireConfigLoaded] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
    const [onboardingStep, setOnboardingStep] = useState<number>(() => {
        const settings = getStoredSettings();
        return settings.onboardingStep || 1;
    });
    const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean>(() => {
        const settings = getStoredSettings();
        return Boolean(settings.onboardingComplete);
    });
    const [onboardingCompletedAt, setOnboardingCompletedAt] = useState<number | null>(() => {
        const settings = getStoredSettings();
        return typeof settings.onboardingCompletedAt === 'number' ? settings.onboardingCompletedAt : null;
    });
    const [theme, setTheme] = useState<ThemeMode>(() => {
        const settings = getStoredSettings();
        return settings.theme === 'light' ? 'light' : 'dark';
    });
    const [lastSnapshot, setLastSnapshot] = useState<FireSnapshotLite | null>(null);
    const quoteWatchlist = useQuoteWatchlist();
    const [deferPortfolioPersist, setDeferPortfolioPersist] = useState(false);
    const [portfolioSearch, setPortfolioSearch] = useState('');
    const [saveState, setSaveState] = useState<{ status: 'idle' | 'pending' | 'saving' | 'success' | 'error'; message?: string }>({
        status: 'idle',
    });
    const [overridesBySymbol, setOverridesBySymbol] = useState<Record<string, AssetOverrides>>({});
    const [portfolioMetaById, setPortfolioMetaById] = useState<Record<string, { name?: string }>>({});
    const [isEditingPortfolioName, setIsEditingPortfolioName] = useState(false);
    const [portfolioNameDraft, setPortfolioNameDraft] = useState('');
    const [showScrollTop, setShowScrollTop] = useState(false);
    const [startEditingHolding, setStartEditingHolding] = useState(false);
    const userMenuRef = useRef<HTMLDivElement | null>(null);
    const trackedSymbolsSyncKeyRef = useRef<string | null>(null);
    const skipNextPortfolioPersistRef = useRef(false);
    const projectIdLoggedRef = useRef(false);
    const liveQuotesRef = useRef<Record<string, MarketQuote>>({});
    const lastQuoteLoadSignatureRef = useRef<string>('');
    const persistOverridesForPortfolio = useCallback((portfolioId: string, overrides: Record<string, AssetOverrides>) => {
        const key = `overrides:${portfolioId}`;
        const serialized = JSON.stringify(overrides);

        try {
            localStorage.setItem(key, serialized);
            return;
        } catch (error) {
            const isQuotaExceeded = error instanceof DOMException && (
                error.name === 'QuotaExceededError' || error.name === 'NS_ERROR_DOM_QUOTA_REACHED'
            );
            if (!isQuotaExceeded) {
                warn('[overrides] failed to persist overrides', { error, key, portfolioId });
                return;
            }

            const entries = Object.entries(overrides);
            if (!entries.length) {
                warn('[overrides] localStorage quota exceeded while persisting empty payload', { key, portfolioId });
                return;
            }

            const trimmed = Object.fromEntries(
                entries
                    .sort(([, left], [, right]) => {
                        const leftTs = Date.parse(String(left?.updatedAt || '')) || 0;
                        const rightTs = Date.parse(String(right?.updatedAt || '')) || 0;
                        return rightTs - leftTs;
                    })
                    .slice(0, Math.max(1, Math.floor(entries.length * 0.7)))
            );

            try {
                localStorage.setItem(key, JSON.stringify(trimmed));
                warn('[overrides] localStorage quota exceeded; persisted trimmed overrides', {
                    key,
                    portfolioId,
                    originalCount: entries.length,
                    retainedCount: Object.keys(trimmed).length,
                });
            } catch (retryError) {
                warn('[overrides] failed to persist trimmed overrides after quota error', {
                    error: retryError,
                    key,
                    portfolioId,
                    originalCount: entries.length,
                    retainedCount: Object.keys(trimmed).length,
                });
            }
        }
    }, []);

    useEffect(() => {
        setLoggingEnabled(true);
    }, []);

    useEffect(() => {
        liveQuotesRef.current = liveQuotes;
    }, [liveQuotes]);

    useEffect(() => {
        if (!user) return;
        setAccountSettings((prev) => ({
            ...prev,
            fullName: prev.fullName || user.name || '',
            email: prev.email || user.email || '',
        }));
    }, [user]);

    const welcomeName = getDashboardDisplayName(user, accountSettings);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 240);
        };
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (projectIdLoggedRef.current) return;
        const projectId = (db as { app?: { options?: { projectId?: string } } })?.app?.options?.projectId;
        if (!projectId) return;
        projectIdLoggedRef.current = true;
        info('[firestore] project', { projectId });
    }, []);

    const handleSelectAsset = useCallback((asset: Asset, startEditing = false) => {
        setSelectedAsset(asset);
        setStartEditingHolding(startEditing);
    }, []);

    const upsertQuoteWatchlist = useCallback(
        (symbols: Array<string | QuoteWatchlistItemInput>) => {
            if (isGuest || !user) return;
            if (!symbols.length) return;
            info('[quoteWatchlist] upsert requested', {
                count: symbols.length,
                userId: user.uid,
                sample: symbols.slice(0, 5).map((symbol) => (typeof symbol === 'string' ? symbol : symbol.symbol)),
            });
            upsertQuoteWatchlistSymbols(user.uid, symbols).catch((error) =>
                warn('[quoteWatchlist] upsert failed', {
                    error,
                    count: symbols.length,
                    userId: user.uid,
                })
            );
        },
        [isGuest, user]
    );

    const removeQuoteWatchlist = useCallback(
        (symbols: string[]) => {
            if (isGuest || !user) return;
            if (!symbols.length) return;
            info('[quoteWatchlist] remove requested', {
                count: symbols.length,
                userId: user.uid,
            });
            removeQuoteWatchlistSymbols(user.uid, symbols).catch((error) =>
                warn('[quoteWatchlist] remove failed', {
                    error,
                    count: symbols.length,
                    userId: user.uid,
                })
            );
        },
        [isGuest, user]
    );

    const collectActiveSymbols = (transactions: Transaction[]) =>
        transactions
            .filter((tx) => !tx.deletedAt)
            .map((tx) => String(tx.symbol || '').trim().toUpperCase())
            .filter(Boolean);

    const buildPortfolioAnalytics = useCallback(
        (transactions: Transaction[]) => buildPortfolioAnalyticsFromTransactions(transactions, selectedCurrency),
        [selectedCurrency]
    );

    const currentPortfolio = portfolios.find((p) => p.id === currentPortfolioId) || portfolios[0] || null;
    const currentPortfolioName =
        (currentPortfolio && portfolioMetaById[currentPortfolio.id]?.name?.trim()) || currentPortfolio?.name || 'No portfolio selected';
    const currentTransactions = useMemo(
        () => (currentPortfolio?.transactions || []).filter((tx) => !tx.deletedAt),
        [currentPortfolio]
    );
    const currencySymbol = getCurrencySymbol(selectedCurrency);
    const showImportActions = view === 'holdings' && Boolean(currentPortfolio);

    useEffect(() => {
        if (!user || isGuest) {
            setPendingDividendPayouts([]);
            return;
        }

        const notificationsRef = collection(db, 'users', user.uid, 'dividend_payout_notifications');
        const notificationsQuery = query(notificationsRef, where('status', '==', 'pending'));

        return onSnapshot(notificationsQuery, (snapshot) => {
            const records = snapshot.docs
                .map((snap) => {
                    const data = snap.data();
                    const quantity = Number(data.quantity) || 0;
                    const perShare = Number(data.amountPerShare) || 0;
                    const estimatedAmount = Number(data.estimatedAmount) || 0;
                    const payDate = String(data.payDate || '').slice(0, 10);
                    const currency = String(data.currency || selectedCurrency).toUpperCase();
                    if (!payDate || !data.symbol || quantity <= 0) return null;

                    return {
                        notificationId: snap.id,
                        symbol: String(data.symbol).toUpperCase(),
                        name: String(data.name || data.symbol || '').trim() || String(data.symbol).toUpperCase(),
                        date: payDate,
                        payDate,
                        quantity,
                        amount: estimatedAmount > 0 ? estimatedAmount : perShare > 0 ? perShare * quantity : 0,
                        perShare: perShare > 0 ? perShare : quantity > 0 && estimatedAmount > 0 ? estimatedAmount / quantity : 0,
                        currency: currency as SupportedCurrency,
                        status: 'pending' as const,
                    };
                })
                .filter((item): item is PendingDividendPayoutRecord => Boolean(item))
                .sort((a, b) => a.payDate.localeCompare(b.payDate) || a.symbol.localeCompare(b.symbol));

            setPendingDividendPayouts(records);
        });
    }, [isGuest, selectedCurrency, user]);

    useEffect(() => {
        const nextMeta: Record<string, { name?: string }> = {};
        portfolios.forEach((portfolio) => {
            const key = `portfolioMeta:${portfolio.id}`;
            const raw = localStorage.getItem(key);
            if (!raw) return;
            try {
                nextMeta[portfolio.id] = JSON.parse(raw);
            } catch {
                return;
            }
        });
        setPortfolioMetaById(nextMeta);
    }, [portfolios]);

    useEffect(() => {
        if (!currentPortfolioId) {
            setOverridesBySymbol({});
            return;
        }
        const raw = localStorage.getItem(`overrides:${currentPortfolioId}`);
        if (!raw) {
            setOverridesBySymbol({});
            return;
        }
        try {
            setOverridesBySymbol(JSON.parse(raw));
        } catch {
            setOverridesBySymbol({});
        }
    }, [currentPortfolioId]);

    useEffect(() => {
        if (!currentPortfolio) return;
        const draftName = (portfolioMetaById[currentPortfolio.id]?.name || currentPortfolio.name || '').trim();
        setPortfolioNameDraft(draftName);
        setIsEditingPortfolioName(false);
    }, [currentPortfolio, portfolioMetaById]);

    const persistPortfolios = useCallback(
        async (
            nextPortfolios: Portfolio[],
            nextSelectedCurrency: SupportedCurrency = selectedCurrency,
            nextPortfolioId: string = currentPortfolioId,
            nextOnboardingStep: number = onboardingStep,
            nextOnboardingComplete: boolean = hasCompletedOnboarding,
            nextOnboardingCompletedAt: number | null = onboardingCompletedAt,
            nextTheme: ThemeMode = theme,
            nextAccountSettings: GeneralAccountSettings = accountSettings
        ) => {
            if (isGuest) return;

            if (!isGuest) {
                localStorage.setItem(
                    'frostball_settings',
                    JSON.stringify({
                        currency: nextSelectedCurrency,
                        lastPortfolioId: nextPortfolioId,
                        onboardingStep: nextOnboardingStep,
                        onboardingComplete: nextOnboardingComplete,
                        onboardingCompletedAt: nextOnboardingCompletedAt,
                        theme: nextTheme,
                        displayName: nextAccountSettings.displayName.trim(),
                        accountSettings: nextAccountSettings,
                    })
                );
            }

            if (user) {
                await syncPortfoliosToFirestore(user.uid, nextPortfolios.map((p) => cleanUndefinedDeep(p) as Portfolio));

                const generalSettingsPayload = {
                    currency: nextSelectedCurrency,
                    lastPortfolioId: nextPortfolioId,
                    onboardingStep: nextOnboardingStep,
                    onboardingComplete: nextOnboardingComplete,
                    onboardingCompletedAt: nextOnboardingCompletedAt,
                    theme: nextTheme,
                    displayName: nextAccountSettings.displayName.trim(),
                    accountSettings: nextAccountSettings,
                };

                await setDoc(doc(db, 'users', user.uid, 'settings', 'general'), generalSettingsPayload);
            }
        },
        [
            currentPortfolioId,
            hasCompletedOnboarding,
            onboardingCompletedAt,
            onboardingStep,
            selectedCurrency,
            theme,
            accountSettings,
            user,
            isGuest,
        ]
    );

    const persistFireConfig = useCallback(
        async (nextConfig: FireConfig) => {
            if (isGuest) return;

            if (user) {
                await setDoc(doc(db, 'users', user.uid, 'settings', 'fire'), nextConfig);
            }
        },
        [user, isGuest]
    );

    const persistGeneralSettings = useCallback(
        async (nextAccountSettings: GeneralAccountSettings) => {
            if (isGuest) return;

            const settingsPayload = {
                currency: selectedCurrency,
                lastPortfolioId: currentPortfolioId,
                onboardingStep,
                onboardingComplete: hasCompletedOnboarding,
                onboardingCompletedAt,
                theme,
                displayName: nextAccountSettings.displayName.trim(),
                accountSettings: nextAccountSettings,
            };

            localStorage.setItem('frostball_settings', JSON.stringify(settingsPayload));

            if (user) {
                await setDoc(doc(db, 'users', user.uid, 'settings', 'general'), settingsPayload, { merge: true });
            }
        },
        [
            currentPortfolioId,
            hasCompletedOnboarding,
            isGuest,
            onboardingCompletedAt,
            onboardingStep,
            selectedCurrency,
            theme,
            user,
        ]
    );

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((firebaseUser) => {
            if (firebaseUser) {
                setIsGuest(false);
                setUser({
                    uid: firebaseUser.uid,
                    displayName: firebaseUser.displayName || '',
                    name: firebaseUser.displayName || '',
                    email: firebaseUser.email || '',
                    photoUrl:
                        firebaseUser.photoURL ||
                        `https://api.dicebear.com/7.x/initials/svg?seed=${firebaseUser.email}&backgroundColor=1e1e26`,
                });
            } else {
                setIsGuest(false);
                setUser(null);
            }
            setAuthLoading(false);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const handleLogout = async () => {
        await logout();
        setIsGuest(false);
        setView('dashboard');
        setPortfolios([]);
        setCurrentPortfolioId('');
        setFireConfig(DEFAULT_FIRE_CONFIG);
        setFireConfigLoaded(false);
        setHasCompletedOnboarding(false);
        setOnboardingStep(1);
        setShowOnboardingWizard(false);
    };

    const handleGuestAccess = () => {
        setIsGuest(true);
        setUser(null);
        setPortfolios([]);
        setCurrentPortfolioId('');
        setFireConfig(DEFAULT_FIRE_CONFIG);
        setFireConfigLoaded(true);
        setIsLoaded(true);
        setHasCompletedOnboarding(false);
        setOnboardingStep(1);
        setShowOnboardingWizard(false);
    };

    useEffect(() => {
        if (authLoading) return;

        let unsubscribePortfolios: (() => void) | undefined;
        let isActive = true;

        const loadUserData = async () => {
            if (!user) {
                setIsLoaded(true);
                setFireConfigLoaded(isGuest);
                setPortfolios([]);
                return;
            }
            setIsLoaded(false);
            setFireConfigLoaded(false);

            const userRef = doc(db, 'users', user.uid);
            const settingsRef = doc(db, 'users', user.uid, 'settings', 'general');
            const fireRef = doc(db, 'users', user.uid, 'settings', 'fire');

            const loadSettingsAndFire = async () => {
                let resolvedGeneralSettings: {
                    currency: SupportedCurrency;
                    lastPortfolioId: string;
                    onboardingStep: number;
                    onboardingComplete: boolean;
                    onboardingCompletedAt: number | null;
                    theme: ThemeMode;
                    accountSettings: GeneralAccountSettings;
                } | null = null;
                let resolvedFireConfig: FireConfig | null = null;

                try {
                    const settingsSnap = await getDoc(settingsRef);
                    if (!isActive) return;

                    if (settingsSnap.exists()) {
                        const data = settingsSnap.data();
                        const nextSettings = {
                            currency: (data.currency as SupportedCurrency) || selectedCurrency,
                            lastPortfolioId: typeof data.lastPortfolioId === 'string' ? data.lastPortfolioId : currentPortfolioId,
                            onboardingStep: typeof data.onboardingStep === 'number' ? data.onboardingStep : onboardingStep,
                            onboardingComplete: typeof data.onboardingComplete === 'boolean' ? data.onboardingComplete : hasCompletedOnboarding,
                            onboardingCompletedAt:
                                typeof data.onboardingCompletedAt === 'number' ? data.onboardingCompletedAt : onboardingCompletedAt,
                            theme: (data.theme === 'light' || data.theme === 'dark' ? data.theme : theme) as ThemeMode,
                            accountSettings: normalizeAccountSettings(
                                { ...(typeof data.accountSettings === 'object' && data.accountSettings ? data.accountSettings : {}), displayName: data.displayName },
                                user
                            ),
                        };

                        resolvedGeneralSettings = nextSettings;
                        setSelectedCurrency(nextSettings.currency);
                        if (nextSettings.lastPortfolioId) setCurrentPortfolioId(nextSettings.lastPortfolioId);
                        setOnboardingStep(nextSettings.onboardingStep);
                        setHasCompletedOnboarding(nextSettings.onboardingComplete);
                        if (typeof nextSettings.onboardingCompletedAt === 'number') {
                            setOnboardingCompletedAt(nextSettings.onboardingCompletedAt);
                        }
                        setTheme(nextSettings.theme);
                        setAccountSettings(nextSettings.accountSettings);
                    } else {
                        const legacyUserSnap = await getDoc(userRef);
                        const legacyData = legacyUserSnap.exists() ? legacyUserSnap.data() : null;
                        const fallbackSettings = {
                            currency: (legacyData?.currency || selectedCurrency) as SupportedCurrency,
                            lastPortfolioId:
                                typeof legacyData?.lastPortfolioId === 'string' ? legacyData.lastPortfolioId : currentPortfolioId,
                            onboardingStep:
                                typeof legacyData?.onboardingStep === 'number' ? legacyData.onboardingStep : onboardingStep,
                            onboardingComplete:
                                typeof legacyData?.onboardingComplete === 'boolean' ? legacyData.onboardingComplete : hasCompletedOnboarding,
                            onboardingCompletedAt:
                                typeof legacyData?.onboardingCompletedAt === 'number'
                                    ? legacyData.onboardingCompletedAt
                                    : onboardingCompletedAt,
                            theme: (legacyData?.theme === 'light' || legacyData?.theme === 'dark' ? legacyData.theme : theme) as ThemeMode,
                            accountSettings: normalizeAccountSettings(
                                { ...(typeof legacyData?.accountSettings === 'object' && legacyData?.accountSettings ? legacyData.accountSettings : {}), displayName: legacyData?.displayName },
                                user
                            ),
                        };

                        resolvedGeneralSettings = fallbackSettings;
                        setSelectedCurrency(fallbackSettings.currency);
                        if (fallbackSettings.lastPortfolioId) setCurrentPortfolioId(fallbackSettings.lastPortfolioId);
                        setOnboardingStep(fallbackSettings.onboardingStep);
                        setHasCompletedOnboarding(fallbackSettings.onboardingComplete);
                        if (typeof fallbackSettings.onboardingCompletedAt === 'number') {
                            setOnboardingCompletedAt(fallbackSettings.onboardingCompletedAt);
                        }
                        setTheme(fallbackSettings.theme);
                        setAccountSettings(fallbackSettings.accountSettings);

                        await setDoc(settingsRef, fallbackSettings);
                    }

                    const fireSnap = await getDoc(fireRef);
                    if (!isActive) return;
                    if (fireSnap.exists()) {
                        const mergedFireConfig = mergeFireConfig(fireSnap.data() as FireConfig);
                        resolvedFireConfig = mergedFireConfig;
                        setFireConfig(mergedFireConfig);
                        if (mergedFireConfig.onboarding?.isComplete) {
                            setHasCompletedOnboarding(true);
                            if (typeof mergedFireConfig.onboarding.completedAt === 'number') {
                                setOnboardingCompletedAt(mergedFireConfig.onboarding.completedAt);
                            }
                        }
                    } else {
                        resolvedFireConfig = DEFAULT_FIRE_CONFIG;
                        setFireConfig(DEFAULT_FIRE_CONFIG);
                    }

                    if (resolvedGeneralSettings) {
                        await setDoc(settingsRef, resolvedGeneralSettings, { merge: true });
                    }
                    await setDoc(fireRef, resolvedFireConfig || DEFAULT_FIRE_CONFIG, { merge: true });
                } catch (e) {
                    console.error('Firestore Load Error', e);
                    setFireConfig(DEFAULT_FIRE_CONFIG);
                } finally {
                    if (isActive) {
                        setFireConfigLoaded(true);
                    }
                }
            };

            loadSettingsAndFire();

            const portfoliosRef = collection(db, 'users', user.uid, 'portfolios');
            unsubscribePortfolios = onSnapshot(
                portfoliosRef,
                (snapshot) => {
                    if (!isActive) return;

                    if (snapshot.empty) {
                        skipNextPortfolioPersistRef.current = true;
                        setPortfolios([]);
                        setIsLoaded(true);
                        return;
                    }

                    loadPortfoliosWithTransactions(user.uid)
                        .then((loadedPortfolios) => {
                            if (!isActive) return;
                            skipNextPortfolioPersistRef.current = true;
                            setPortfolios(loadedPortfolios);
                            setIsLoaded(true);
                        })
                        .catch((error) => {
                            console.error('Portfolio subcollection load error', error);
                            if (!isActive) return;
                            const fallbackPortfolios: Portfolio[] = snapshot.docs
                                .map((docSnap) => normalizePortfolio({ id: docSnap.id, ...docSnap.data() }))
                                .filter((portfolio): portfolio is Portfolio => Boolean(portfolio));
                            skipNextPortfolioPersistRef.current = true;
                            setPortfolios(fallbackPortfolios);
                            setIsLoaded(true);
                        });
                },
                (error) => {
                    console.error('Firestore Load Error', error);
                    if (isActive) {
                        setPortfolios([]);
                        setIsLoaded(true);
                    }
                }
            );
        };

        loadUserData();

        return () => {
            isActive = false;
            if (unsubscribePortfolios) unsubscribePortfolios();
        };
    }, [user, authLoading, isGuest]);

    useEffect(() => {
        if (!isLoaded) return;

        if (skipNextPortfolioPersistRef.current) {
            skipNextPortfolioPersistRef.current = false;
            return;
        }

        if (deferPortfolioPersist) return;

        const timeoutId = setTimeout(() => {
            persistPortfolios(portfolios).catch((e) => console.error('Firestore Save Error', e));
        }, 1500);
        return () => clearTimeout(timeoutId);
    }, [
        portfolios,
        selectedCurrency,
        currentPortfolioId,
        user,
        isLoaded,
        onboardingStep,
        hasCompletedOnboarding,
        onboardingCompletedAt,
        persistPortfolios,
        deferPortfolioPersist,
    ]);

    useEffect(() => {
        if (!isLoaded || !fireConfigLoaded) return;

        const timeoutId = setTimeout(() => {
            persistFireConfig(fireConfig).catch((e) => console.error('FIRE Save Error', e));
        }, 1500);

        return () => clearTimeout(timeoutId);
    }, [fireConfig, fireConfigLoaded, isLoaded, persistFireConfig]);

    const handleCreatePortfolio = () => {
        if (portfolios.length >= 10) {
            alert('Maximum of 10 portfolios reached.');
            return;
        }
        const name = prompt('Enter Portfolio Name:');
        if (name) {
            const newId = Math.random().toString(36).substr(2, 9);
            const newPortfolio: Portfolio = {
                id: newId,
                name: name,
                transactions: [],
                created: Date.now(),
            };
            setPortfolios([...portfolios, newPortfolio]);
            setCurrentPortfolioId(newId);
            setShowPortfolioMenu(false);
        }
    };

    const handleClearPortfolio = () => {
        if (!currentPortfolio) return;

        if (window.confirm(`Are you sure you want to clear all data for "${currentPortfolioName}"?`)) {
            const activeSymbolsInCurrent = new Set(collectActiveSymbols(currentPortfolio.transactions || []));
            if (activeSymbolsInCurrent.size) {
                const activeSymbolsInOtherPortfolios = new Set(
                    portfolios
                        .filter((portfolio) => portfolio.id !== currentPortfolioId)
                        .flatMap((portfolio) => collectActiveSymbols(portfolio.transactions || []))
                );
                const symbolsToRemove = Array.from(activeSymbolsInCurrent).filter(
                    (symbol) => !activeSymbolsInOtherPortfolios.has(symbol)
                );
                if (symbolsToRemove.length) {
                    removeQuoteWatchlist(symbolsToRemove);
                }
            }
            setPortfolios(portfolios.map((p) => (p.id === currentPortfolioId ? { ...p, transactions: [] } : p)));
        }
    };

    const handleDeletePortfolio = async () => {
        if (!currentPortfolio) return;

        const message = `Delete "${currentPortfolioName}" and all of its data? This cannot be undone and will permanently remove all transactions. Your totals will be recalculated after deletion.`;
        if (!window.confirm(message)) return;

        const typedConfirmation = window.prompt(
            `Type DELETE (or the portfolio name: ${currentPortfolioName}) to permanently remove this portfolio and save this deletion.`
        );
        const normalizedTypedConfirmation = typedConfirmation?.trim();
        const hasDeleteConfirmation = normalizedTypedConfirmation?.toUpperCase() === 'DELETE';
        const hasNameConfirmation = normalizedTypedConfirmation === currentPortfolioName;
        if (!hasDeleteConfirmation && !hasNameConfirmation) {
            alert('Deletion cancelled. Please type DELETE or the exact portfolio name to confirm.');
            return;
        }

        const portfolioId = currentPortfolio.id;

        if (!isGuest && user) {
            try {
                const subcollections = ['holdings', 'import_holding', 'import_holdings', 'imports'];
                for (const subcollection of subcollections) {
                    const docsSnap = await getDocs(collection(db, 'users', user.uid, 'portfolios', portfolioId, subcollection));
                    if (!docsSnap.empty) {
                        for (let i = 0; i < docsSnap.docs.length; i += 400) {
                            const batch = writeBatch(db);
                            docsSnap.docs.slice(i, i + 400).forEach((entry) => batch.delete(entry.ref));
                            await batch.commit();
                        }
                    }
                }
                await deleteDoc(doc(db, 'users', user.uid, 'portfolios', portfolioId));
            } catch (error) {
                console.error('Portfolio delete failed', error);
                alert('Failed to delete portfolio from cloud storage. Please try again.');
                return;
            }
        }

        const activeSymbolsInPortfolio = new Set(collectActiveSymbols(currentPortfolio.transactions || []));
        if (activeSymbolsInPortfolio.size) {
            const activeSymbolsInOtherPortfolios = new Set(
                portfolios
                    .filter((portfolio) => portfolio.id !== portfolioId)
                    .flatMap((portfolio) => collectActiveSymbols(portfolio.transactions || []))
            );
            const symbolsToRemove = Array.from(activeSymbolsInPortfolio).filter(
                (symbol) => !activeSymbolsInOtherPortfolios.has(symbol)
            );
            if (symbolsToRemove.length) {
                removeQuoteWatchlist(symbolsToRemove);
            }
        }
        localStorage.removeItem(`portfolioMeta:${portfolioId}`);
        localStorage.removeItem(`overrides:${portfolioId}`);
        setPortfolioMetaById((prev) => {
            const { [portfolioId]: _removed, ...rest } = prev;
            return rest;
        });
        setPortfolioNameDraft('');
        setIsEditingPortfolioName(false);
        setShowPortfolioMenu(false);
        setPortfolios((prev) => {
            const remainingPortfolios = prev.filter((p) => p.id !== portfolioId);
            if (currentPortfolioId === portfolioId) {
                setCurrentPortfolioId(remainingPortfolios[0]?.id || '');
            }
            return remainingPortfolios;
        });
    };

    const applyImportedTransactions = (newTransactions: Transaction[], sourceLabel: string) => {
        if (!currentPortfolio) {
            alert('Create a portfolio before importing transactions.');
            return;
        }

        if (newTransactions.length > 0) {
            const currentTx = currentPortfolio.transactions;
            const existingIds = new Set(currentTx.map((t) => t.id));
            const normalizedIncoming = newTransactions
                .map((tx) =>
                    normalizeTransaction({
                        ...tx,
                        source: tx.source || 'csv',
                        sourceLabel: tx.sourceLabel || sourceLabel,
                    })
                )
                .filter((tx): tx is Transaction => Boolean(tx));
            const uniqueNewTx = normalizedIncoming.filter((t) => !existingIds.has(t.id));

            if (uniqueNewTx.length > 0) {
                setPortfolios((prev) =>
                    prev.map((p) =>
                        p.id === currentPortfolioId
                            ? { ...p, transactions: [...p.transactions, ...uniqueNewTx] }
                            : p
                    )
                );
                upsertQuoteWatchlist(uniqueNewTx.map((tx) => tx.symbol));
                setDeferPortfolioPersist(true);
                setSaveState({
                    status: 'pending',
                    message: `Loaded ${uniqueNewTx.length} transactions from ${sourceLabel}. Click "Save imports" to sync.`,
                });
                alert(`Successfully imported ${uniqueNewTx.length} new transactions from ${sourceLabel}.`);
            } else {
                alert('No new transactions found. All entries in this file already exist.');
            }
        } else {
            alert(`Could not parse transactions from ${sourceLabel}. Check file format.`);
        }
    };

    const handleManualTransactionSave = (transaction: Transaction, addMore?: boolean) => {
        if (!currentPortfolio) {
            alert('Create a portfolio before adding transactions.');
            return;
        }

        setPortfolios((prev) => {
            const next = prev.map((p) =>
                p.id === currentPortfolioId
                    ? {
                          ...p,
                          transactions: [
                              ...(p.transactions || []),
                              normalizeTransaction({
                                  ...transaction,
                                  source: transaction.source || 'manual',
                                  sourceLabel: transaction.sourceLabel || 'Added manually',
                              }) || transaction,
                          ],
                      }
                    : p
            );
            persistPortfolios(next).catch((e) => console.error('Immediate save error', e));
            return next;
        });
        upsertQuoteWatchlist([transaction.symbol]);

        if (!addMore) {
            setShowTransactionModal(false);
        }
    };

    const handleSmartImport = (transactions: Transaction[], sourceLabel: string) => {
        applyImportedTransactions(transactions, sourceLabel);
        setShowUploadModal(false);
    };

    const handleSaveImportedPortfolios = async () => {
        if (!currentPortfolio) return;
        setSaveState({ status: 'saving', message: 'Saving changes…' });
        try {
            await persistPortfolios(portfolios);
            setDeferPortfolioPersist(false);
            setSaveState({ status: 'success', message: 'Portfolio saved successfully.' });
            const savedSymbols = (currentPortfolio?.transactions || [])
                .filter((tx) => !tx.deletedAt)
                .map((tx) => tx.symbol);
            if (savedSymbols.length) {
                upsertQuoteWatchlist(savedSymbols);
            }
        } catch (error) {
            console.error('Manual save failed', error);
            setSaveState({
                status: 'error',
                message: error instanceof Error ? error.message : 'Unable to save right now. Try again.',
            });
        }
    };

    const allTransactions = useMemo(
        () => portfolios.flatMap((p) => (p.transactions || []).filter((tx) => !tx.deletedAt)),
        [portfolios]
    );

    const handleUpdateFireConfig = (nextConfig: FireConfig) => {
        setFireConfig(nextConfig);
        setFireConfigLoaded(true);
    };

    const currentPortfolioAnalytics = useMemo(
        () => buildPortfolioAnalytics(currentTransactions),
        [buildPortfolioAnalytics, currentTransactions]
    );

    const consolidatedAnalytics = useMemo(
        () => buildPortfolioAnalytics(allTransactions),
        [allTransactions, buildPortfolioAnalytics]
    );

    useEffect(() => {
        const trackedItems: QuoteWatchlistItemInput[] = consolidatedAnalytics.assets
            .filter((asset) => Boolean(asset.symbol) && asset.symbol.toUpperCase() !== 'CASH')
            .map((asset) => ({
                symbol: asset.symbol.toUpperCase(),
                name: asset.name,
                exchange: asset.exchange,
                currency: String(asset.quoteCurrency || asset.priceCurrency || asset.currency || selectedCurrency).toUpperCase(),
                assetType: asset.type === 'ETF' ? 'etf' : asset.type === 'Crypto' ? 'crypto' : 'equity',
            }));

        if (!trackedItems.length) {
            trackedSymbolsSyncKeyRef.current = null;
            return;
        }

        const nextKey = trackedItems
            .map((item) => `${item.symbol}:${String(item.exchange || '')}`)
            .sort()
            .join('|');

        if (nextKey === trackedSymbolsSyncKeyRef.current) return;
        trackedSymbolsSyncKeyRef.current = nextKey;
        upsertQuoteWatchlist(trackedItems);
    }, [consolidatedAnalytics.assets, selectedCurrency, upsertQuoteWatchlist]);

    const applyLiveQuotes = useCallback(
        (items: Asset[]) =>
            items.map((asset) => {
                const quote = resolveLiveQuoteForAsset(asset, liveQuotes);
                if (!quote) return asset;

                const quoteCurrencyRaw = String(quote.currency || asset.quoteCurrency || asset.priceCurrency || selectedCurrency).toUpperCase();
                const quoteCurrency =
                    resolveEffectiveQuoteCurrency({
                        reportedCurrency: quoteCurrencyRaw,
                        exchange: asset.exchange,
                        price: quote.price,
                        referencePrice: asset.currentPriceNative || asset.currentPrice || asset.avgPriceNative || asset.avgPrice,
                    }) || quoteCurrencyRaw;
                const holdingCurrencyRaw = String(asset.priceCurrency || asset.quoteCurrency || selectedCurrency).toUpperCase();
                const holdingCurrency = holdingCurrencyRaw === 'GBX' ? 'GBP' : holdingCurrencyRaw;
                const priceInSelected = convertCurrency(quote.price, quoteCurrency, selectedCurrency);
                const priceInHoldingCurrency = convertCurrency(quote.price, quoteCurrency, holdingCurrency);
                const quoteScaleToBase = inferQuoteScaleToBase(holdingCurrency, asset.baseCurrency);

                return {
                    ...asset,
                    currentPrice: priceInSelected,
                    currentPriceNative: priceInHoldingCurrency,
                    priceUpdatedAt: quote.asOf,
                    quoteCurrency: holdingCurrency,
                    quoteScaleToBase,
                    priceCurrency: holdingCurrency,
                    dataProviderId: quote.providerId || asset.dataProviderId,
                };
            }),
        [liveQuotes, selectedCurrency]
    );

    const resolveLiveQuote = useCallback(
        (asset: Asset) => resolveLiveQuoteForAsset(asset, liveQuotes),
        [liveQuotes]
    );

    const symbolsWithTradeHistory = useMemo(() => {
        const symbols = new Set<string>();

        currentTransactions.forEach((tx) => {
            const symbol = String(tx.symbol || '').trim().toUpperCase();
            if (!symbol) return;

            const action = String(tx.action || '').trim().toLowerCase();
            if (action.includes('buy') || action.includes('sell')) {
                symbols.add(symbol);
            }
        });

        return symbols;
    }, [currentTransactions]);

    const applyOverrides = useCallback(
        (items: Asset[]) =>
            items.map((asset) => {
                const override = overridesBySymbol[asset.symbol];
                if (!override) return asset;

                const hasTradeHistory = symbolsWithTradeHistory.has(asset.symbol.toUpperCase());

                return {
                    ...asset,
                    overrides: override,
                    quantity: hasTradeHistory ? asset.quantity : override.quantity ?? asset.quantity,
                    avgPrice: hasTradeHistory ? asset.avgPrice : override.avgPrice ?? asset.avgPrice,
                    firstBuyDate: override.firstBuyDate ?? asset.firstBuyDate,
                };
            }),
        [overridesBySymbol, symbolsWithTradeHistory]
    );

    const portfolioAssets = useMemo(() => {
        const withQuotes = applyLiveQuotes(currentPortfolioAnalytics.assets);
        return applyOverrides(withQuotes);
    }, [applyLiveQuotes, applyOverrides, currentPortfolioAnalytics.assets]);
    const assetsRaw = consolidatedAnalytics.assets;
    const assets = useMemo(
        () => applyLiveQuotes(consolidatedAnalytics.assets),
        [applyLiveQuotes, consolidatedAnalytics.assets]
    );

    const dailyHoldingsStats = useMemo(() => {
        let totalDailyPnL = 0;
        let totalPreviousValue = 0;
        let latestQuoteTimeMs = 0;
        const maxQuoteAgeMs = 7 * 24 * 60 * 60 * 1000;
        const nowMs = Date.now();
        const movers: { symbol: string; name: string; dailyPnL: number; dailyPct: number; quoteTime?: string }[] = [];

        assets
            .filter((asset) => asset.symbol !== 'CASH' && asset.quantity > 0)
            .forEach((asset) => {
                const quote = resolveLiveQuote(asset);
                const quoteTime = quote?.asOf || quote?.fetchedAt || asset.priceUpdatedAt;
                const quoteTimeMs = quoteTime ? new Date(quoteTime).getTime() : NaN;
                if (!Number.isFinite(quoteTimeMs) || nowMs - quoteTimeMs > maxQuoteAgeMs) return;

                const currentPrice = Number(asset.currentPrice);
                if (!Number.isFinite(currentPrice) || currentPrice <= 0) return;

                const quoteCurrencyRaw = String(quote?.currency || asset.quoteCurrency || asset.priceCurrency || selectedCurrency).toUpperCase();
                const quoteCurrency =
                    resolveEffectiveQuoteCurrency({
                        reportedCurrency: quoteCurrencyRaw,
                        exchange: asset.exchange,
                        price: quote?.price,
                        referencePrice: asset.currentPrice || asset.currentPriceNative || asset.avgPrice || asset.avgPriceNative,
                    }) || quoteCurrencyRaw;
                const quotePct = Number(quote?.changePercent);
                const hasUsableQuotePct = Number.isFinite(quotePct) && quotePct > -99.9;

                let previousCloseSelected = 0;
                let dailyPct = 0;

                if (hasUsableQuotePct) {
                    previousCloseSelected = currentPrice / (1 + quotePct / 100);
                    dailyPct = quotePct;
                } else {
                    const previousClose = Number(quote?.previousClose);
                    if (!Number.isFinite(previousClose)) return;

                    previousCloseSelected = convertCurrency(previousClose, quoteCurrency, selectedCurrency);
                    if (!Number.isFinite(previousCloseSelected) || previousCloseSelected <= 0) return;

                    dailyPct = ((currentPrice - previousCloseSelected) / previousCloseSelected) * 100;
                }

                if (!Number.isFinite(previousCloseSelected) || previousCloseSelected <= 0 || !Number.isFinite(dailyPct)) return;

                const dailyChangePerShare = currentPrice - previousCloseSelected;
                const dailyPnL = dailyChangePerShare * asset.quantity;

                totalDailyPnL += dailyPnL;
                totalPreviousValue += previousCloseSelected * asset.quantity;
                latestQuoteTimeMs = Math.max(latestQuoteTimeMs, quoteTimeMs);
                movers.push({ symbol: asset.symbol, name: asset.name || asset.symbol, dailyPnL, dailyPct, quoteTime });
            });

        const totalDailyPct = totalPreviousValue > 0 ? (totalDailyPnL / totalPreviousValue) * 100 : 0;
        const latestQuoteTime = latestQuoteTimeMs > 0 ? new Date(latestQuoteTimeMs).toISOString() : null;
        return { totalDailyPnL, totalDailyPct, movers, latestQuoteTime };
    }, [assets, resolveLiveQuote, selectedCurrency]);

    const topMovers = useMemo(() => {
        const movers = [...dailyHoldingsStats.movers].filter((mover) => Number.isFinite(mover.dailyPct) && Number.isFinite(mover.dailyPnL));
        const byCurrencyImpactThenPct = (a: (typeof movers)[number], b: (typeof movers)[number]) =>
            Math.abs(b.dailyPnL) - Math.abs(a.dailyPnL) || Math.abs(b.dailyPct) - Math.abs(a.dailyPct);
        const gainers = movers
            .filter((mover) => mover.dailyPnL > 0)
            .sort(byCurrencyImpactThenPct)
            .slice(0, 3);
        const losers = movers
            .filter((mover) => mover.dailyPnL < 0)
            .sort(byCurrencyImpactThenPct)
            .slice(0, 3);
        return { gainers, losers };
    }, [dailyHoldingsStats.movers]);

    const topMoversQuoteTimeLabel = useMemo(() => {
        if (!dailyHoldingsStats.latestQuoteTime) return null;
        return new Date(dailyHoldingsStats.latestQuoteTime).toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    }, [dailyHoldingsStats.latestQuoteTime]);

    const portfolioSnapshot = useMemo(() => {
        const activeAssets = assets.filter((asset) => asset.symbol !== 'CASH' && asset.quantity > 0);
        const largestHolding = [...activeAssets].sort((a, b) => b.quantity * b.currentPrice - a.quantity * a.currentPrice)[0] || null;
        const highestDividendReceived = [...activeAssets].sort((a, b) => (b.totalDividends || b.historicDividendsReceived || 0) - (a.totalDividends || a.historicDividendsReceived || 0))[0] || null;
        return {
            largestHolding,
            bestMover: topMovers.gainers[0] || null,
            worstMover: topMovers.losers[0] || null,
            highestDividendReceived,
        };
    }, [assets, topMovers.gainers, topMovers.losers]);

    const aiInsightBullets = useMemo(() => {
        const snapshotBullets = analystSnapshot?.sections
            ?.flatMap((section) => section.items || [])
            .map((item) => item.text)
            .filter(Boolean) || [];
        if (snapshotBullets.length) return snapshotBullets.slice(0, 3);

        const text = portfolioInsight ? portfolioInsight.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
        if (text) return [text.slice(0, 140) + (text.length > 140 ? '…' : '')];

        if (assets.length > 0) {
            return ['Run a fresh analysis to review diversification, income quality and FIRE alignment.'];
        }
        return ['Import holdings to unlock AI portfolio diagnostics.'];
    }, [analystSnapshot, assets.length, portfolioInsight]);

    const todayPnLDateLabel = useMemo(
        () =>
            new Date().toLocaleDateString(undefined, {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
            }),
        []
    );


    const formatCompactCurrency = useCallback(
        (amount: number, options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }) => {
            const absoluteAmount = Math.abs(amount);
            const sign = amount < 0 ? '-' : '';
            const format = (value: number, suffix = '') =>
                `${sign}${currencySymbol}${value.toLocaleString(undefined, {
                    minimumFractionDigits: options?.minimumFractionDigits,
                    maximumFractionDigits: options?.maximumFractionDigits ?? (suffix ? 1 : 0),
                })}${suffix}`;

            if (absoluteAmount >= 1_000_000_000) return format(absoluteAmount / 1_000_000_000, 'bn');
            if (absoluteAmount >= 1_000_000) return format(absoluteAmount / 1_000_000, 'm');
            if (absoluteAmount >= 10_000) return format(absoluteAmount / 1_000, 'k');
            return format(absoluteAmount);
        },
        [currencySymbol]
    );
    const monthlyDividends = consolidatedAnalytics.monthlyDividends;
    const dataRange = consolidatedAnalytics.dateRange;
    const portfolioMonthlyDividends = currentPortfolioAnalytics.monthlyDividends;
    const portfolioDataRange = currentPortfolioAnalytics.dateRange;
    const normalizedPortfolioSearch = portfolioSearch.trim().toLowerCase();
    const filteredPortfolioAssets = useMemo(() => {
        if (!normalizedPortfolioSearch) {
            return portfolioAssets;
        }

        return portfolioAssets.filter((asset) => {
            const symbol = asset.symbol?.toLowerCase() || '';
            const name = asset.name?.toLowerCase() || '';
            return symbol.includes(normalizedPortfolioSearch) || name.includes(normalizedPortfolioSearch);
        });
    }, [portfolioAssets, normalizedPortfolioSearch]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredPortfolioAssets.length / holdingsPageSize) - 1);
        setHoldingsPage((currentPage) => Math.min(currentPage, maxPage));
    }, [filteredPortfolioAssets.length, holdingsPageSize]);

    const handleLoadAnalystSnapshot = useCallback(async () => {
        setIsLoadingAnalystSnapshot(true);
        setAnalystSnapshotError(null);
        setPortfolioInsightError(null);

        const [snapshotResult, insightResult] = await Promise.allSettled([
            fetchAnalystSnapshot(),
            analyzePortfolio(assets),
        ]);

        if (snapshotResult.status === 'fulfilled') {
            const snapshot = snapshotResult.value;
            setAnalystSnapshot(snapshot);
        } else {
            setAnalystSnapshotError(snapshotResult.reason instanceof Error ? snapshotResult.reason.message : 'Unable to load AI analyst snapshot');
        }

        if (insightResult.status === 'fulfilled') {
            setPortfolioInsight(insightResult.value);
        } else {
            setPortfolioInsightError(insightResult.reason instanceof Error ? insightResult.reason.message : 'Unable to load portfolio health analysis');
        }

        setIsLoadingAnalystSnapshot(false);
    }, [assets]);

    const totalHoldingsPages = Math.max(1, Math.ceil(filteredPortfolioAssets.length / holdingsPageSize));

    const displayedPortfolioAssets = useMemo(
        () =>
            filteredPortfolioAssets.slice(
                holdingsPage * holdingsPageSize,
                holdingsPage * holdingsPageSize + holdingsPageSize
            ),
        [filteredPortfolioAssets, holdingsPage, holdingsPageSize]
    );
    const visibleHoldingsStart = displayedPortfolioAssets.length > 0 ? holdingsPage * holdingsPageSize + 1 : 0;
    const visibleHoldingsEnd = displayedPortfolioAssets.length > 0 ? holdingsPage * holdingsPageSize + displayedPortfolioAssets.length : 0;

    const transactionSuggestions = useMemo(() => {
        const entries = new Map<string, { symbol: string; name: string }>();
        const addEntry = (symbol?: string, name?: string) => {
            const normalizedSymbol = symbol?.trim().toUpperCase();
            if (!normalizedSymbol) return;
            if (!entries.has(normalizedSymbol)) {
                entries.set(normalizedSymbol, {
                    symbol: normalizedSymbol,
                    name: name?.trim() || normalizedSymbol,
                });
            }
        };

        currentTransactions.forEach((tx) => addEntry(tx.symbol, tx.name));
        portfolioAssets.forEach((asset) => addEntry(asset.symbol, asset.name));

        return Array.from(entries.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
    }, [currentTransactions, portfolioAssets]);

    useEffect(() => {
        if (!selectedAsset) return;
        const updated = portfolioAssets.find((asset) => asset.symbol === selectedAsset.symbol);
        if (updated && updated !== selectedAsset) {
            setSelectedAsset(updated);
        }
    }, [portfolioAssets, selectedAsset]);


    const globalDividendHoldings = useMemo(
        () => consolidatedAnalytics.assets
            .filter((asset) => asset.quantity > 0 && asset.type !== 'Cash' && asset.symbol)
            .map((asset) => ({
                symbol: asset.symbol.trim().toUpperCase(),
                name: asset.name,
                quantity: asset.quantity,
                currency: asset.currency || asset.priceCurrency || asset.quoteCurrency || asset.baseCurrency || null,
                baseCurrency: asset.baseCurrency || selectedCurrency,
                aliases: Array.from(new Set([
                    asset.dataProviderId,
                    asset.canonicalQuoteKey,
                    asset.symbol?.split('.')[0],
                ].map((value) => String(value || '').trim().toUpperCase()).filter(Boolean))),
            })),
        [consolidatedAnalytics.assets, selectedCurrency]
    );

    const currentDividendHoldings = useMemo(
        () => portfolioAssets
            .filter((asset) => asset.quantity > 0 && asset.type !== 'Cash' && asset.symbol)
            .map((asset) => ({
                symbol: asset.symbol.trim().toUpperCase(),
                name: asset.name,
                quantity: asset.quantity,
                currency: asset.currency || asset.priceCurrency || asset.quoteCurrency || asset.baseCurrency || null,
                baseCurrency: asset.baseCurrency || selectedCurrency,
                aliases: Array.from(new Set([
                    asset.dataProviderId,
                    asset.canonicalQuoteKey,
                    asset.symbol?.split('.')[0],
                ].map((value) => String(value || '').trim().toUpperCase()).filter(Boolean))),
            })),
        [portfolioAssets, selectedCurrency]
    );

    const dividendQuerySymbols = useMemo(
        () => buildDividendQuerySymbolsFromHoldings(globalDividendHoldings),
        [globalDividendHoldings]
    );

    const holdingsSymbolsKey = useMemo(
        () => dividendQuerySymbols.join('|'),
        [dividendQuerySymbols]
    );

    const globalDividendFxRates = useMemo(() => {
        const rates: Record<string, number> = {};
        ['GBP', 'USD', 'EUR', 'GBX'].forEach((fromCurrency) => {
            rates[`${fromCurrency}${selectedCurrency}`] = convertCurrency(1, fromCurrency, selectedCurrency);
            rates[`${fromCurrency}_${selectedCurrency}`] = convertCurrency(1, fromCurrency, selectedCurrency);
        });
        return rates;
    }, [selectedCurrency]);

    useEffect(() => {
        const symbols = holdingsSymbolsKey ? holdingsSymbolsKey.split('|') : [];
        let cancelled = false;

        if (!symbols.length) {
            setGlobalDividendEvents([]);
            setProjectedDividendEvents([]);
            setGlobalDividendEventsError(null);
            setIsLoadingGlobalDividendEvents(false);
            if (isDevelopment()) {
                console.debug('[dividends-ui] global-events-loaded', {
                    symbolsCount: 0,
                    eventsCount: 0,
                    error: null,
                });
            }
            return;
        }

        setIsLoadingGlobalDividendEvents(true);
        setGlobalDividendEventsError(null);

        Promise.all([
            loadGlobalDividendEventsForSymbols({ db, symbols, horizonDays: 365, now: new Date() }),
            loadProjectedDividendEventsForSymbols({ db, symbols }),
        ])
            .then(([events, projected]) => {
                if (cancelled) return;
                setGlobalDividendEvents(events);
                setProjectedDividendEvents(projected);
                setGlobalDividendEventsError(null);
                if (isDevelopment()) {
                    console.debug('[dividends-ui] global-events-loaded', {
                        symbolsCount: symbols.length,
                        eventsCount: events.length,
                        error: null,
                    });
                }
            })
            .catch((error) => {
                if (cancelled) return;
                const message = error instanceof Error ? error.message : String(error);
                setGlobalDividendEvents([]);
                setProjectedDividendEvents([]);
            setProjectedDividendEvents([]);
                setGlobalDividendEventsError(message);
                if (isDevelopment()) {
                    console.debug('[dividends-ui] global-events-loaded', {
                        symbolsCount: symbols.length,
                        eventsCount: 0,
                        error: message,
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoadingGlobalDividendEvents(false);
            });

        return () => {
            cancelled = true;
        };
    }, [holdingsSymbolsKey]);

    const emptyDividendForecast = useMemo<UserDividendForecastResult>(() => buildUserDividendForecast({
        holdings: [],
        globalEvents: [],
        projectedEvents: [],
        selectedCurrency,
        horizonMonths: 12,
        fxRates: globalDividendFxRates,
        now: new Date(),
    }), [globalDividendFxRates, selectedCurrency]);

    const dividendForecast = useMemo(
        () => buildUserDividendForecast({
            holdings: globalDividendHoldings,
            globalEvents: globalDividendEvents,
            projectedEvents: projectedDividendEvents,
            selectedCurrency,
            horizonMonths: 12,
            fxRates: globalDividendFxRates,
            now: new Date(),
        }),
        [globalDividendEvents, projectedDividendEvents, globalDividendFxRates, globalDividendHoldings, selectedCurrency]
    );

    const currentDividendForecast = useMemo(
        () => currentDividendHoldings.length
            ? buildUserDividendForecast({
                holdings: currentDividendHoldings,
                globalEvents: globalDividendEvents,
                projectedEvents: projectedDividendEvents,
                selectedCurrency,
                horizonMonths: 12,
                fxRates: globalDividendFxRates,
                now: new Date(),
            })
            : emptyDividendForecast,
        [currentDividendHoldings, emptyDividendForecast, globalDividendEvents, projectedDividendEvents, globalDividendFxRates, selectedCurrency]
    );

    const applyQuote = useCallback((quoteKey: string, q: MarketQuote, options?: { providerKey?: string; symbol?: string; instrumentId?: string }) => {
        const canonicalKey = normalizeQuoteKey(q.providerId || quoteKey);
        const rawKey = normalizeQuoteKey(quoteKey);
        const aliasKeys = getQuoteAliasKeysFromRequest(quoteKey, q.providerId, options?.providerKey, options?.symbol, options?.instrumentId);

        setLiveQuotes((prev) => {
            const next = {
                ...prev,
                ...(canonicalKey ? { [canonicalKey]: q } : {}),
                ...(rawKey && rawKey !== canonicalKey ? { [rawKey]: q } : {}),
                ...(((q as any).canonicalQuoteKey) ? { [normalizeQuoteKey((q as any).canonicalQuoteKey)]: q } : {}),
                ...Object.fromEntries(aliasKeys.map((key) => [key, q])),
            };
            liveQuotesRef.current = next;
            return next;
        });
    }, []);

    const loadCachedQuotes = useCallback(
        async (items: Asset[]) => {
            const quoteRequests = new Map<string, CachedQuoteRequest>();
            const seenInstrumentIds = new Set<string>();
            const instrumentQuoteCandidates = new Map<string, Asset>();
            const requestOwners = new Map<string, Asset[]>();

            const trackRequestOwner = (requestKey: string, asset: Asset) => {
                const owners = requestOwners.get(requestKey) || [];
                owners.push(asset);
                requestOwners.set(requestKey, owners);
            };

            const logQuoteRequestDecision = (asset: Asset, details: {
                symbol: string;
                instrumentId: string;
                providerKey?: string | null;
                queuedRequest: boolean;
                queuedRequestKey: string | null;
                reason?: string;
            }) => {
                debugPortfolioQuotes('[portfolio-quotes] request decision', {
                    symbol: details.symbol,
                    instrumentId: details.instrumentId,
                    assetProviderId: asset.dataProviderId,
                    assetExchange: asset.exchange,
                    assetCurrency: asset.currency,
                    assetNativeCurrency: asset.nativeCurrency,
                    providerKey: details.providerKey ?? null,
                    queuedRequest: details.queuedRequest,
                    queuedRequestKey: details.queuedRequestKey,
                    reason: details.reason,
                });
            };

            const logAppliedQuoteForAsset = (asset: Asset, quoteKey: string, quote: MarketQuote, options?: { providerKey?: string; symbol?: string; instrumentId?: string }) => {
                const canonicalKey = normalizeQuoteKey(quote.providerId || quoteKey);
                const rawKey = normalizeQuoteKey(quoteKey);
                const aliasKeys = getQuoteAliasKeysFromRequest(quoteKey, quote.providerId, options?.providerKey, options?.symbol, options?.instrumentId);
                const appliedKeys = Array.from(new Set([canonicalKey, rawKey, ...aliasKeys].filter(Boolean)));
                console.log('[portfolio-quotes] applied aliases', {
                    symbol: asset.symbol,
                    instrumentId: asset.instrumentId,
                    quoteKey,
                    appliedKeys,
                });
                const resolvedQuote = resolveLiveQuoteForAsset(asset, liveQuotesRef.current);
                console.log('[portfolio-quotes] resolved final quote', {
                    symbol: asset.symbol,
                    instrumentId: asset.instrumentId,
                    found: Boolean(resolvedQuote),
                    resolvedProviderId: resolvedQuote?.providerId,
                });
                debugPortfolioQuotes('[portfolio-quotes] final quote selected', {
                    symbol: asset.symbol,
                    instrumentId: asset.instrumentId,
                    requestedProviderKey: options?.providerKey || quoteKey,
                    resolvedProviderId: resolvedQuote?.providerId,
                    quotePrice: resolvedQuote?.price,
                    quoteRawPrice: resolvedQuote?.rawPrice,
                    quoteCurrency: resolvedQuote?.currency,
                    quoteRawCurrency: resolvedQuote?.rawCurrency,
                    quoteUnit: (resolvedQuote as any)?.quoteUnit,
                    unitScale: resolvedQuote?.unitScale,
                    source: (resolvedQuote as any)?.source,
                    allAliasKeys: appliedKeys,
                });
            };

            for (const asset of items) {
                try {
                    const instrumentId = String(asset.instrumentId || '').trim();
                    const providerId = String(asset.dataProviderId || '').trim().toUpperCase();
                    const symbol = String(asset.symbol || '').trim().toUpperCase();

                    console.log('[portfolio-quotes] asset start', {
                        symbol,
                        instrumentId,
                        providerId,
                    });

                    if (instrumentId) {
                        let queuedRequest = false;
                        let queuedRequestKey: string | null = null;
                        if (seenInstrumentIds.has(instrumentId)) {
                            console.log('[portfolio-quotes] queued request', {
                                symbol,
                                instrumentId,
                                providerKey: null,
                                queuedRequest,
                                queuedRequestKey,
                                reason: 'duplicate instrumentId',
                            });
                            logQuoteRequestDecision(asset, {
                                symbol,
                                instrumentId,
                                providerKey: null,
                                queuedRequest,
                                queuedRequestKey,
                                reason: 'duplicate instrumentId',
                            });
                            continue;
                        }
                        seenInstrumentIds.add(instrumentId);
                        instrumentQuoteCandidates.set(instrumentId, asset);
                        queuedRequest = true;
                        queuedRequestKey = `instrument:${instrumentId}`;

                        console.log('[portfolio-quotes] queued request', {
                            symbol,
                            instrumentId,
                            providerKey: null,
                            queuedRequest,
                            queuedRequestKey,
                            reason: 'instrumentId backend routing',
                        });
                        logQuoteRequestDecision(asset, {
                            symbol,
                            instrumentId,
                            providerKey: null,
                            queuedRequest,
                            queuedRequestKey,
                            reason: 'instrumentId backend routing',
                        });

                        continue;
                    }

                    const request = buildQuoteProviderRequest(asset);
                    if (!request) {
                        console.log('[portfolio-quotes] queued request', {
                            symbol,
                            instrumentId,
                            providerKey: null,
                            queuedRequest: false,
                            queuedRequestKey: null,
                        });
                        logQuoteRequestDecision(asset, {
                            symbol,
                            instrumentId,
                            providerKey: null,
                            queuedRequest: false,
                            queuedRequestKey: null,
                            reason: 'no provider request',
                        });
                        continue;
                    }
                    const queuedRequestKey = `${request.source}:${request.providerValue}`;
                    quoteRequests.set(queuedRequestKey, request);
                    trackRequestOwner(queuedRequestKey, asset);
                    console.log('[portfolio-quotes] queued request', {
                        symbol,
                        instrumentId,
                        providerKey: null,
                        queuedRequest: true,
                        queuedRequestKey,
                    });
                    logQuoteRequestDecision(asset, {
                        symbol,
                        instrumentId,
                        providerKey: null,
                        queuedRequest: true,
                        queuedRequestKey,
                        reason: 'fallback request builder',
                    });
                } catch (error) {
                    console.warn('Quote hydration prep failed for asset; continuing with remaining assets', {
                        symbol: asset?.symbol,
                        instrumentId: asset?.instrumentId,
                        dataProviderId: asset?.dataProviderId,
                        error,
                    });
                }
            }

            for (const [instrumentId, asset] of instrumentQuoteCandidates) {
                if (resolveLiveQuoteForAsset(asset, liveQuotesRef.current)) {
                    continue;
                }

                try {
                    const instrumentQuoteFromCacheEndpoint = await fetchCachedQuoteByInstrumentKey(instrumentId, { force: true });
                    console.log('[portfolio-quotes] fetched quote', {
                        symbol: asset.symbol,
                        instrumentId,
                        method: 'fetchCachedQuoteByInstrumentKey',
                        called: true,
                        returnedQuote: Boolean(instrumentQuoteFromCacheEndpoint),
                    });
                    if (instrumentQuoteFromCacheEndpoint) {
                        applyQuote(`instrument:${instrumentId}`, instrumentQuoteFromCacheEndpoint, {
                            symbol: asset.symbol,
                        });
                        logAppliedQuoteForAsset(asset, `instrument:${instrumentId}`, instrumentQuoteFromCacheEndpoint, {
                            symbol: asset.symbol,
                        });
                        continue;
                    }

                    const instrumentQuote = await getQuoteForInstrument(instrumentId);
                    console.log('[portfolio-quotes] fetched quote', {
                        symbol: asset.symbol,
                        instrumentId,
                        method: 'getQuoteForInstrument',
                        called: true,
                        returnedQuote: Boolean(instrumentQuote),
                    });
                    if (instrumentQuote) {
                        applyQuote(`instrument:${instrumentId}`, instrumentQuote, {
                            symbol: asset.symbol,
                        });
                        logAppliedQuoteForAsset(asset, `instrument:${instrumentId}`, instrumentQuote, {
                            symbol: asset.symbol,
                        });
                        continue;
                    }
                } catch (error) {
                    console.warn('Instrument quote fetch failed for portfolio preload', { instrumentId, symbol: asset.symbol, error });
                }
            }

            for (const [requestKey, { source, providerValue }] of quoteRequests) {
                const aliasKeys = getQuoteAliasKeysFromRequest(requestKey);
                const hasQuote = aliasKeys.some((key) => Boolean(liveQuotesRef.current[key]));
                if (hasQuote) {
                    continue;
                }

                try {
                    console.log('[portfolio-quotes] fetched quote', {
                        requestKey,
                        getLegacyQuoteCalled: source === 'legacy',
                        getQuoteByProviderKeyCalled: source !== 'legacy' && source !== 'instrument',
                    });
                    const quote =
                        source === 'legacy'
                            ? await getLegacyQuote(providerValue)
                            : source === 'instrument'
                                ? await fetchCachedQuoteByInstrumentKey(providerValue, { force: true })
                                : await getQuoteByProviderKey(source, providerValue);
                    console.log('[portfolio-quotes] fetched quote', {
                        requestKey,
                        getLegacyQuoteCalled: source === 'legacy',
                        getQuoteByProviderKeyCalled: source !== 'legacy' && source !== 'instrument',
                        returnedQuote: Boolean(quote),
                    });
                    if (quote) {
                        applyQuote(requestKey, quote, {
                            providerKey: `${source}:${providerValue}`,
                        });
                        const ownerAssets = requestOwners.get(requestKey) || [];
                        ownerAssets.forEach((asset) => {
                            logAppliedQuoteForAsset(asset, requestKey, quote, {
                                providerKey: `${source}:${providerValue}`,
                            });
                        });
                    }
                } catch (error) {
                    console.warn('Quote fetch failed for request', { requestKey, source, providerValue, error });
                }
            }
        },
        [applyQuote, user?.uid]
    );

    useEffect(() => {
        const sourceAssets = currentPortfolioAnalytics.assets.length
            ? currentPortfolioAnalytics.assets
            : consolidatedAnalytics.assets;
        const quoteLoadSignature = sourceAssets
            .map((asset) => {
                const instrumentId = String(asset.instrumentId || '').trim();
                const providerId = String(asset.dataProviderId || '').trim().toUpperCase();
                const symbol = String(asset.symbol || '').trim().toUpperCase();
                return instrumentId || providerId || symbol;
            })
            .filter(Boolean)
            .sort()
            .join('|');

        if (!quoteLoadSignature || quoteLoadSignature === lastQuoteLoadSignatureRef.current) {
            return;
        }

        lastQuoteLoadSignatureRef.current = quoteLoadSignature;
        void loadCachedQuotes(sourceAssets);
    }, [consolidatedAnalytics.assets, currentPortfolioAnalytics.assets, loadCachedQuotes]);

    const handleResolveUnresolvedInstrument = useCallback((asset: Asset) => {
        const toFiniteNumber = (value: unknown): number | null => {
            const parsed = Number(value);
            return Number.isFinite(parsed) ? parsed : null;
        };

        const symbol = asset.symbol.toUpperCase();
        const symbolTransactions = (currentPortfolio?.transactions || [])
            .filter((tx) => !tx.deletedAt && tx.symbol.toUpperCase() === symbol)
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

        const latestTrade = symbolTransactions.find((tx) => tx.action === 'Buy' || tx.action === 'Sell');
        const fallbackQuantity = symbolTransactions.reduce((sum, tx) => {
            const quantity = Number(tx.quantity || tx.shares || 0);
            if (!Number.isFinite(quantity)) return sum;
            if (tx.action === 'Buy') return sum + quantity;
            if (tx.action === 'Sell') return sum - quantity;
            return sum;
        }, 0);

        const draftQuantity = toFiniteNumber(asset.quantity) ?? (fallbackQuantity > 0 ? fallbackQuantity : null);
        const draftPrice =
            toFiniteNumber(asset.avgPriceNative) ?? toFiniteNumber(asset.avgPrice) ?? toFiniteNumber(latestTrade?.price) ?? null;
        const draftPriceCurrency =
            asset.priceCurrency || latestTrade?.priceCurrency || latestTrade?.currency || selectedCurrency;

        const debugPayload = {
            symbol: asset.symbol,
            instrumentId: asset.instrumentId || null,
            dataProviderId: asset.dataProviderId || null,
            isin: asset.isin || null,
            exchange: asset.exchange || null,
            priceUpdatedAt: asset.priceUpdatedAt || null,
            draftQuantity,
            draftPrice,
            draftPriceCurrency,
        };
        console.warn('[instrument-resolution] open transaction modal to resolve', debugPayload);
        setTransactionModalDraft({
            symbol,
            name: asset.name,
            exchange: asset.exchange,
            quantity: draftQuantity ?? undefined,
            price: draftPrice ?? undefined,
            priceCurrency: draftPriceCurrency,
        });
        setShowTransactionModal(true);
        window.alert(
            `Unresolved instrument for ${asset.symbol}.

` +
                `How to debug:
` +
                `1) Open browser console and look for [instrument-resolution] logs.
` +
                `2) Edit or add a transaction for this holding.
` +
                `3) Search and select the exact instrument so instrumentId is saved.
` +
                `4) Refresh quotes and verify that the stale warning disappears.`
        );
    }, [currentPortfolio?.transactions, selectedCurrency]);

    const saveOverride = useCallback(
        (symbol: string, patch: AssetOverrides) => {
            if (!currentPortfolioId) return;
            setOverridesBySymbol((prev) => {
                const existing = prev[symbol] || {};
                const merged: AssetOverrides = {
                    ...existing,
                    ...patch,
                    updatedAt: new Date().toISOString(),
                };

                Object.entries(merged).forEach(([key, value]) => {
                    if (value === undefined || value === '') {
                        delete merged[key as keyof AssetOverrides];
                    }
                });

                const hasMeaningfulOverride = Object.keys(merged).some((key) => key !== 'updatedAt');
                const next = { ...prev };
                if (hasMeaningfulOverride) {
                    next[symbol] = merged;
                } else {
                    delete next[symbol];
                }
                persistOverridesForPortfolio(currentPortfolioId, next);
                return next;
            });
        },
        [currentPortfolioId, persistOverridesForPortfolio]
    );

    const handleDeleteHolding = useCallback(
        (asset: Asset) => {
            if (!currentPortfolioId || !currentPortfolio) return;
            const symbol = asset.symbol.toUpperCase();
            const now = new Date().toISOString();
            const deletedBy = user?.uid;
            const symbolStillActive = portfolios.some((portfolio) => {
                if (portfolio.id === currentPortfolioId) return false;
                return (portfolio.transactions || []).some(
                    (tx) => !tx.deletedAt && tx.symbol.toUpperCase() === symbol
                );
            });
            if (!symbolStillActive) {
                removeQuoteWatchlist([symbol]);
            }

            setPortfolios((prev) => {
                const next = prev.map((portfolio) => {
                    if (portfolio.id !== currentPortfolioId) return portfolio;
                    const nextTransactions = (portfolio.transactions || []).map((tx) => {
                        if (tx.symbol.toUpperCase() !== symbol) return tx;
                        if (tx.deletedAt) return tx;
                        return {
                            ...tx,
                            deletedAt: now,
                            deletedBy,
                        };
                    });
                    return { ...portfolio, transactions: nextTransactions };
                });
                persistPortfolios(next).catch((e) => console.error('Holding delete failed', e));
                return next;
            });

            setOverridesBySymbol((prev) => {
                if (!prev[symbol]) return prev;
                const next = { ...prev };
                delete next[symbol];
                persistOverridesForPortfolio(currentPortfolioId, next);
                return next;
            });

            if (selectedAsset?.symbol.toUpperCase() === symbol) {
                setSelectedAsset(null);
            }
        },
        [
            currentPortfolio,
            currentPortfolioId,
            persistOverridesForPortfolio,
            persistPortfolios,
            portfolios,
            removeQuoteWatchlist,
            selectedAsset,
            user?.uid,
        ]
    );


    const handleUpdateTransaction = useCallback(
        (updatedTransaction: Transaction) => {
            if (!currentPortfolioId) return;
            const normalized = normalizeTransaction(updatedTransaction);
            if (!normalized) return;

            setPortfolios((prev) => {
                const next = prev.map((portfolio) => {
                    if (portfolio.id !== currentPortfolioId) return portfolio;
                    const nextTransactions = (portfolio.transactions || []).map((tx) =>
                        tx.id === normalized.id ? { ...tx, ...normalized, deletedAt: undefined, deletedBy: undefined } : tx
                    );
                    return { ...portfolio, transactions: nextTransactions };
                });
                persistPortfolios(next).catch((e) => console.error('Transaction update failed', e));
                return next;
            });

            upsertQuoteWatchlist([normalized.symbol]);
        },
        [currentPortfolioId, persistPortfolios, upsertQuoteWatchlist]
    );

    const handleDeleteTransaction = useCallback(
        (transactionId: string) => {
            if (!currentPortfolioId) return;
            const now = new Date().toISOString();
            const deletedBy = user?.uid;

            setPortfolios((prev) => {
                const next = prev.map((portfolio) => {
                    if (portfolio.id !== currentPortfolioId) return portfolio;
                    const nextTransactions = (portfolio.transactions || []).map((tx) => {
                        if (tx.id !== transactionId || tx.deletedAt) return tx;
                        return {
                            ...tx,
                            deletedAt: now,
                            deletedBy,
                        };
                    });
                    return { ...portfolio, transactions: nextTransactions };
                });
                persistPortfolios(next).catch((e) => console.error('Transaction delete failed', e));
                return next;
            });
        },
        [currentPortfolioId, persistPortfolios, user?.uid]
    );

    const getPortfolioDisplayName = useCallback(
        (portfolio: Portfolio) => portfolioMetaById[portfolio.id]?.name?.trim() || portfolio.name,
        [portfolioMetaById]
    );

    const handleSavePortfolioName = useCallback(() => {
        if (!currentPortfolioId) return;
        const trimmed = portfolioNameDraft.trim();
        const key = `portfolioMeta:${currentPortfolioId}`;

        if (!trimmed) {
            localStorage.removeItem(key);
            setPortfolioMetaById((prev) => {
                const next = { ...prev };
                delete next[currentPortfolioId];
                return next;
            });
            setIsEditingPortfolioName(false);
            return;
        }

        const nextMeta = {
            ...(portfolioMetaById[currentPortfolioId] || {}),
            name: trimmed,
        };
        localStorage.setItem(key, JSON.stringify(nextMeta));
        setPortfolioMetaById((prev) => ({ ...prev, [currentPortfolioId]: nextMeta }));
        setIsEditingPortfolioName(false);
    }, [currentPortfolioId, portfolioMetaById, portfolioNameDraft]);

    const calculateAssetMarketValue = (asset: Asset) => {
        const currentValue = asset.quantity * asset.currentPrice;
        if (Number.isFinite(currentValue) && currentValue > 0) {
            return currentValue;
        }
        if (asset.avgPrice > 0) {
            return asset.quantity * asset.avgPrice;
        }
        if ((asset.totalInvested || 0) > 0) {
            return asset.totalInvested || 0;
        }
        return 0;
    };

    const combinedMetrics = useMemo(
        () => calculatePortfolioIncomeMetrics({ holdings: assets, range: dataRange, calculateAssetMarketValue }),
        [assets, dataRange]
    );
    const currentPortfolioMetrics = useMemo(
        () => calculatePortfolioIncomeMetrics({ holdings: portfolioAssets, range: portfolioDataRange, calculateAssetMarketValue }),
        [portfolioAssets, portfolioDataRange]
    );
    const {
        totalValue,
        totalInvested,
        totalDividendsReceived,
        totalProfit,
        totalReturnPct,
        historicalYieldOnCost,
    } = combinedMetrics;
    const portfolioCashValue = useMemo(
        () => assets.filter((asset) => asset.type === 'Cash').reduce((sum, asset) => sum + calculateAssetMarketValue(asset), 0),
        [assets]
    );
    const {
        totalValue: portfolioTotalValue,
        totalInvested: portfolioTotalInvested,
        totalDividendsReceived: portfolioDividendsReceived,
        totalProfit: portfolioTotalProfit,
        totalReturnPct: portfolioReturnPct,
    } = currentPortfolioMetrics;

    const estimatedDividendFromYield = useMemo(
        () =>
            assets.reduce((sum, asset) => {
                const dividendYield = asset.dividendYield ?? 0;
                if (dividendYield <= 0) return sum;
                const positionValue = asset.quantity * asset.currentPrice;
                return sum + positionValue * (dividendYield / 100);
            }, 0),
        [assets]
    );

    const projectedPortfolioDividend = dividendForecast.next12mTotal;

    const annualRetirementSpend = fireConfig.retirementExpenses || fireConfig.annualExpenses;
    const canonicalNetWorth = useMemo(
        () => buildCanonicalNetWorth(fireConfig, combinedMetrics.totalValue),
        [fireConfig, combinedMetrics.totalValue]
    );
    const pensionPotTotal = useMemo(
        () =>
            fireConfig.manualAssets
                .filter((asset) => asset.type === 'Pension')
                .reduce((sum, asset) => sum + (asset.value || 0), 0),
        [fireConfig.manualAssets]
    );
    const currentNetWorth = canonicalNetWorth.total - canonicalNetWorth.assets.pensions + pensionPotTotal;
    const netWorthAssetsTotal =
        canonicalNetWorth.assets.portfolio +
        canonicalNetWorth.assets.manual +
        canonicalNetWorth.assets.cash +
        canonicalNetWorth.assets.property +
        pensionPotTotal;

    const netWorthAssetSegments = useMemo(
        () => [
            { label: 'Stocks', value: canonicalNetWorth.assets.portfolio, color: '#38bdf8' },
            { label: 'Pension', value: pensionPotTotal, color: '#0ea5e9' },
            { label: 'Fixed assets', value: canonicalNetWorth.assets.property, color: '#0284c7' },
            { label: 'Cash', value: canonicalNetWorth.assets.cash, color: '#94a3b8' },
            { label: 'Other assets', value: canonicalNetWorth.assets.manual, color: '#475569' },
        ],
        [
            canonicalNetWorth.assets.cash,
            canonicalNetWorth.assets.manual,
            canonicalNetWorth.assets.portfolio,
            canonicalNetWorth.assets.property,
            pensionPotTotal,
        ]
    );
    const fireTarget = fireConfig.withdrawalRate > 0 ? (annualRetirementSpend * 100) / fireConfig.withdrawalRate : 0;
    const fireTargetTooltip =
        fireConfig.withdrawalRate > 0
            ? `FIRE target = annual retirement spend ÷ withdrawal rate. With ${currencySymbol}${annualRetirementSpend.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
              })} and ${fireConfig.withdrawalRate}%, target is ${currencySymbol}${fireTarget.toLocaleString(undefined, {
                  maximumFractionDigits: 0,
              })}.`
            : 'Set a withdrawal rate above 0% to calculate your FIRE target.';

    const snapshotsWithAge = useMemo(
        () => getSnapshotsWithAge(fireConfig.snapshots, fireConfig.currentAge),
        [fireConfig.snapshots, fireConfig.currentAge]
    );

    const { startAge: journeyStartAge } = useMemo(
        () => getJourneyStart(snapshotsWithAge, fireConfig.currentAge, currentNetWorth),
        [snapshotsWithAge, fireConfig.currentAge, currentNetWorth]
    );
    const journeyDurationLabel = useMemo(
        () => formatJourneyDuration(fireConfig.snapshots, fireConfig.lastUpdated, onboardingCompletedAt),
        [fireConfig.snapshots, fireConfig.lastUpdated, onboardingCompletedAt]
    );

    const fireWealthBreakdown = useMemo(
        () => computeFireWealthBreakdown(fireConfig, combinedMetrics.totalValue),
        [fireConfig, combinedMetrics.totalValue]
    );
    const capitalProgress = fireTarget > 0 ? currentNetWorth / fireTarget : 0;
    const fireProgress = Math.min(100, Math.max(0, capitalProgress * 100));
    const liquidFireProgress = Math.min(100, Math.max(0, safeDivide(fireWealthBreakdown.liquidFireAssetsToday, fireTarget) * 100));
    const dashboardDividendIncomeKpi = buildDashboardDividendIncomeKpi({
        totalDividendsReceived,
        projectedForwardDividend: dividendForecast.next12mTotal,
        annualExpenses: fireConfig.annualExpenses,
        formatCurrency: formatCompactCurrency,
    });
    const netWorthSnapshotsSorted = useMemo(
        () => [...(fireConfig.snapshots || [])].sort((a, b) => a.date.localeCompare(b.date)),
        [fireConfig.snapshots]
    );
    const initialNetWorthSnapshot = netWorthSnapshotsSorted[0] || null;
    const netWorthChangeSinceStart = initialNetWorthSnapshot ? currentNetWorth - initialNetWorthSnapshot.totalNetWorth : 0;
    const netWorthChangeSinceStartPct =
        initialNetWorthSnapshot && initialNetWorthSnapshot.totalNetWorth !== 0
            ? (netWorthChangeSinceStart / Math.abs(initialNetWorthSnapshot.totalNetWorth)) * 100
            : 0;
    const hasNetWorthBaseline = Boolean(initialNetWorthSnapshot);
    const hasFireAssets = fireConfig.manualAssets.length + fireConfig.pensions.length > 0;
    const onboardingChecklist = useMemo(
        () => [
            {
                label: 'Set your current age and retirement age',
                done: fireConfig.currentAge > 0 && fireConfig.retirementAge > fireConfig.currentAge,
            },
            {
                label: 'Set your annual and retirement expenses',
                done: fireConfig.annualExpenses > 0 && fireConfig.retirementExpenses > 0,
            },
            {
                label: 'Define return, inflation, and withdrawal assumptions',
                done: fireConfig.investmentReturnRate > 0 && fireConfig.inflationRate >= 0 && fireConfig.withdrawalRate > 0,
            },
            {
                label: 'Select your country for regulation-aware guidance',
                done: Boolean(fireConfig.country) && fireConfig.country !== 'Other',
            },
            {
                label: 'Add at least one income stream',
                done: fireConfig.incomeStreams.length > 0,
            },
            {
                label: 'Add at least one manual FIRE asset',
                done: fireConfig.manualAssets.length > 0,
            },
            {
                label: 'Add liabilities (if applicable)',
                done: fireConfig.liabilities.length > 0,
            },
            {
                label: 'Capture your first net-worth snapshot',
                done: fireConfig.snapshots.length > 0,
            },
        ],
        [
            fireConfig.annualExpenses,
            fireConfig.currentAge,
            fireConfig.incomeStreams.length,
            fireConfig.inflationRate,
            fireConfig.investmentReturnRate,
            fireConfig.country,
            fireConfig.liabilities.length,
            fireConfig.manualAssets.length,
            fireConfig.retirementAge,
            fireConfig.retirementExpenses,
            fireConfig.snapshots.length,
            fireConfig.withdrawalRate,
        ]
    );
    const onboardingChecklistCompletedCount = onboardingChecklist.filter((item) => item.done).length;
    const isFireOnboardingComplete = onboardingChecklistCompletedCount >= onboardingChecklist.length;
    const onboardingChecklistPercent = Math.round((onboardingChecklistCompletedCount / onboardingChecklist.length) * 100);
    const onboardingRingFill = Math.max(0, Math.min(100, onboardingChecklistPercent));
    const fireSetupCompletion = useMemo(
        () =>
            calculateFireSetupCompletion({
                user,
                accountSettings,
                fireConfig,
                currentNetWorth,
                holdings: assets,
                totalDividendsReceived,
                dividendForecastEvents: dividendForecast.upcomingEvents.map((event) => ({ symbol: event.symbol })),
                legacyOnboardingComplete: Boolean(fireConfig.onboarding?.isComplete || hasCompletedOnboarding),
            }),
        [accountSettings, assets, currentNetWorth, dividendForecast.upcomingEvents, fireConfig, hasCompletedOnboarding, totalDividendsReceived, user]
    );
    const onboardingWizardStepCompletion = useMemo(
        () => [
            fireConfig.currentAge > 0 && fireConfig.retirementAge > fireConfig.currentAge,
            Boolean(fireConfig.country) && fireConfig.country !== 'Other',
            fireConfig.manualAssets.length > 0,
            fireConfig.incomeStreams.length > 0,
            fireConfig.annualExpenses > 0 && fireConfig.retirementExpenses > 0,
            fireConfig.liabilities.length > 0,
            fireConfig.investmentReturnRate > 0 && fireConfig.inflationRate >= 0 && fireConfig.withdrawalRate > 0,
            true,
        ],
        [
            fireConfig.annualExpenses,
            fireConfig.currentAge,
            fireConfig.country,
            fireConfig.incomeStreams.length,
            fireConfig.inflationRate,
            fireConfig.investmentReturnRate,
            fireConfig.liabilities.length,
            fireConfig.manualAssets.length,
            fireConfig.retirementAge,
            fireConfig.retirementExpenses,
            fireConfig.withdrawalRate,
        ]
    );
    const firstIncompleteOnboardingStep =
        onboardingWizardStepCompletion.findIndex((isDone) => !isDone) === -1
            ? ONBOARDING_STEPS
            : onboardingWizardStepCompletion.findIndex((isDone) => !isDone) + 1;

    const requiredNetWorthToday = requiredNetWorthAtAge({
        age: fireConfig.currentAge,
        retirementAge: fireConfig.retirementAge,
        fireTarget,
        currentAge: fireConfig.currentAge,
        currentNetWorth,
    });

    const netWorthGap = currentNetWorth - requiredNetWorthToday;
    const netWorthGapAbs = Math.abs(netWorthGap);
    const gapTolerance = fireTarget * 0.05;
    const isOnTrackWindow = fireTarget > 0 && netWorthGapAbs < gapTolerance;

    useEffect(() => {
        if (!currentNetWorth || !fireProgress) return;

        const snap: FireSnapshotLite = {
            netWorth: currentNetWorth,
            fireProgress,
            timestamp: Date.now(),
        };

        localStorage.setItem('fire_last_snapshot', JSON.stringify(snap));
        setLastSnapshot(snap);
    }, [currentNetWorth, fireProgress]);

    useEffect(() => {
        if (!hasCompletedOnboarding || !Number.isFinite(currentNetWorth)) return;

        const todayIso = new Date().toISOString().slice(0, 10);

        setFireConfig((prev) => {
            const snapshots = prev.snapshots || [];
            const existingIndex = snapshots.findIndex((snapshot) => snapshot.date === todayIso);
            const nextSnapshot = {
                date: todayIso,
                totalNetWorth: currentNetWorth,
                liquidAssets: combinedMetrics.totalValue,
            };

            if (existingIndex >= 0) {
                const existingSnapshot = snapshots[existingIndex];
                if (
                    existingSnapshot.totalNetWorth === nextSnapshot.totalNetWorth &&
                    existingSnapshot.liquidAssets === nextSnapshot.liquidAssets
                ) {
                    return prev;
                }

                const nextSnapshots = [...snapshots];
                nextSnapshots[existingIndex] = nextSnapshot;
                return {
                    ...prev,
                    snapshots: nextSnapshots,
                    lastUpdated: Date.now(),
                };
            }

            return {
                ...prev,
                snapshots: [...snapshots, nextSnapshot],
                lastUpdated: Date.now(),
            };
        });
    }, [hasCompletedOnboarding, currentNetWorth, combinedMetrics.totalValue]);

    const upcomingDividends = useMemo<DividendEvent[]>(
        () => dividendForecast.upcomingEvents.map((event) => ({
            id: event.id,
            symbol: event.symbol,
            name: event.name,
            date: `${event.eventDate}T00:00:00.000Z`,
            eventDate: event.eventDate,
            exDate: event.exDate || undefined,
            payDate: event.paymentDate || undefined,
            amount: event.expectedCashBase,
            cashAmountBase: event.expectedCashBase,
            currency: event.baseCurrency,
            frequency: event.frequency || undefined,
            source: event.source,
            dateType: event.dateType,
            declared: event.declared,
        })),
        [dividendForecast.upcomingEvents]
    );

    const currentUpcomingDividends = useMemo<DividendEvent[]>(
        () => currentDividendForecast.upcomingEvents.map((event) => ({
            id: event.id,
            symbol: event.symbol,
            name: event.name,
            date: `${event.eventDate}T00:00:00.000Z`,
            eventDate: event.eventDate,
            exDate: event.exDate || undefined,
            payDate: event.paymentDate || undefined,
            amount: event.expectedCashBase,
            cashAmountBase: event.expectedCashBase,
            currency: event.baseCurrency,
            frequency: event.frequency || undefined,
            source: event.source,
            dateType: event.dateType,
            declared: event.declared,
        })),
        [currentDividendForecast.upcomingEvents]
    );

    const portfolioPassiveIncome = useMemo(
        () => calculatePassiveIncomeMetrics(currentPortfolioMetrics, currentUpcomingDividends),
        [currentPortfolioMetrics, currentUpcomingDividends]
    );

    const selectedPortfolioHeaderCards = useMemo(
        () =>
            buildSelectedPortfolioHeaderCards({
                currencySymbol,
                totalValue: portfolioTotalValue,
                totalInvested: portfolioTotalInvested,
                totalProfit: portfolioTotalProfit,
                totalReturnPct: portfolioReturnPct,
                dividendsReceived: portfolioDividendsReceived,
                passiveIncome: portfolioPassiveIncome,
                transactions: currentTransactions,
                portfolioCurrency: selectedCurrency,
            }),
        [
            currencySymbol,
            portfolioTotalValue,
            portfolioTotalInvested,
            portfolioTotalProfit,
            portfolioReturnPct,
            portfolioDividendsReceived,
            portfolioPassiveIncome,
            currentTransactions,
            selectedCurrency,
        ]
    );

    const heldSymbolsForAll = useMemo(
        () =>
            new Set(
                assets
                    .filter((asset) => asset.quantity > 0 && asset.type !== 'Cash')
                    .map((asset) => asset.symbol.toUpperCase())
            ),
        [assets]
    );

    const heldSymbolsForPortfolio = useMemo(
        () =>
            new Set(
                portfolioAssets
                    .filter((asset) => asset.quantity > 0 && asset.type !== 'Cash')
                    .map((asset) => asset.symbol.toUpperCase())
            ),
        [portfolioAssets]
    );

    const pendingDividendPayoutsForAllPartitioned = useMemo(
        () => partitionPendingPayoutsByHeldSymbols(pendingDividendPayouts, heldSymbolsForAll),
        [heldSymbolsForAll, pendingDividendPayouts]
    );

    const inferredTodayDividendPayoutsForAll = useMemo(() => {
        const todayIso = toIsoDate(startOfUtcDay(new Date()));
        const existingKeys = new Set(
            pendingDividendPayouts.map((payout) => `${payout.symbol.toUpperCase()}::${payout.payDate}`)
        );

        return upcomingDividends
            .filter((event) => ['FMP', 'FMP_CALENDAR', 'FMP_SYMBOL', 'Market'].includes(event.source || ''))
            .map((event) => {
                const symbol = (event.symbol || '').toUpperCase();
                const payDate = (event.payDate || event.date || '').slice(0, 10);
                if (!symbol || payDate !== todayIso) return null;
                if (existingKeys.has(`${symbol}::${payDate}`)) return null;

                const asset = assets.find((item) => item.symbol.toUpperCase() === symbol && item.quantity > 0);
                if (!asset) return null;

                const quantity = Math.max(Number(asset.quantity) || 0, 0);
                const amount = Math.max(coerceNumber(event.amount, 0), 0);
                const perShare = quantity > 0 ? amount / quantity : 0;

                return {
                    notificationId: `inferred_${symbol}_${payDate}`,
                    symbol,
                    name: asset.name || symbol,
                    date: payDate,
                    payDate,
                    quantity,
                    amount,
                    perShare,
                    currency: selectedCurrency,
                    status: 'pending' as const,
                };
            })
            .filter((item): item is PendingDividendPayoutRecord => Boolean(item));
    }, [assets, pendingDividendPayouts, selectedCurrency, upcomingDividends]);

    const pendingDividendPayoutsForPortfolio = useMemo(
        () => partitionPendingPayoutsByHeldSymbols(pendingDividendPayouts, heldSymbolsForPortfolio).active,
        [heldSymbolsForPortfolio, pendingDividendPayouts]
    );

    const inferredTodayDividendPayoutsForPortfolio = useMemo(
        () => inferredTodayDividendPayoutsForAll.filter((payout) => heldSymbolsForPortfolio.has(payout.symbol.toUpperCase())),
        [heldSymbolsForPortfolio, inferredTodayDividendPayoutsForAll]
    );

    const pendingDividendPayoutsForAll = useMemo(
        () => [...pendingDividendPayoutsForAllPartitioned.active, ...inferredTodayDividendPayoutsForAll],
        [inferredTodayDividendPayoutsForAll, pendingDividendPayoutsForAllPartitioned.active]
    );

    const pendingDividendPayoutsForPortfolioDisplay = useMemo(
        () => [...pendingDividendPayoutsForPortfolio, ...inferredTodayDividendPayoutsForPortfolio],
        [inferredTodayDividendPayoutsForPortfolio, pendingDividendPayoutsForPortfolio]
    );

    useEffect(() => {
        if (!user || isGuest) return;
        if (!pendingDividendPayoutsForAllPartitioned.stale.length) return;

        const staleIds = pendingDividendPayoutsForAllPartitioned.stale
            .map((payout) => payout.notificationId)
            .filter(Boolean);

        if (!staleIds.length) return;

        const batch = writeBatch(db);
        staleIds.forEach((notificationId) => {
            const payoutRef = doc(db, 'users', user.uid, 'dividend_payout_notifications', notificationId);
            batch.set(
                payoutRef,
                {
                    status: 'dismissed',
                    dismissedAt: serverTimestamp(),
                    dismissedReason: 'position_closed_or_portfolio_deleted',
                },
                { merge: true }
            );
        });

        batch.commit().catch((error) => {
            warn('[dividendPayouts] stale notification cleanup failed', {
                error,
                count: staleIds.length,
                userId: user.uid,
            });
        });
    }, [isGuest, pendingDividendPayoutsForAllPartitioned.stale, user]);

    const handleConfirmDividendPayout = useCallback(
        (draft: DividendPayoutDraft) => {
            const confirmedAmount = Number(Math.max(draft.amount, 0).toFixed(2));
            const confirmedPerShare = Number(Math.max(draft.perShare, 0).toFixed(4));
            const transactionId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            handleManualTransactionSave(
                {
                    id: transactionId,
                    date: draft.date,
                    action: 'Dividend',
                    symbol: draft.symbol,
                    name: draft.name,
                    quantity: Math.max(draft.quantity, 0),
                    price: confirmedPerShare,
                    priceCurrency: draft.currency,
                    amount: confirmedAmount,
                    currency: draft.currency,
                    source: 'manual',
                    sourceLabel: 'Dividend payout confirmation',
                },
                false
            );

            if (!user || isGuest || !draft.notificationId) return;

            const payoutRef = doc(db, 'users', user.uid, 'dividend_payout_notifications', draft.notificationId);
            setDoc(
                payoutRef,
                {
                    status: 'confirmed',
                    confirmedAt: serverTimestamp(),
                    confirmedTransactionId: transactionId,
                    confirmedAmount,
                    confirmedPerShare,
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            ).catch((error) => {
                console.error('Failed to mark dividend payout confirmation as complete', error);
            });
        },
        [handleManualTransactionSave, isGuest, user]
    );

    const markOnboardingComplete = useCallback(() => {
        const completedAt = onboardingCompletedAt ?? Date.now();
        setHasCompletedOnboarding(true);
        setOnboardingCompletedAt(completedAt);
        setFireConfig((prev) => ({
            ...prev,
            onboarding: {
                isComplete: true,
                completedAt,
            },
            lastUpdated: Date.now(),
        }));
        return completedAt;
    }, [onboardingCompletedAt]);

    const handleAdvanceOnboarding = () => {
        setOnboardingStep((prev) => {
            const next = Math.min(prev + 1, ONBOARDING_STEPS);
            if (next >= ONBOARDING_STEPS) {
                markOnboardingComplete();
                setShowOnboardingWizard(false);
            }
            return next;
        });
    };

    const handleCompleteOnboarding = () => {
        if (!isFireOnboardingComplete) {
            setHasCompletedOnboarding(false);
            setOnboardingStep(firstIncompleteOnboardingStep);
            return;
        }
        markOnboardingComplete();
        setOnboardingStep(ONBOARDING_STEPS);
        setShowOnboardingWizard(false);
    };

    const handleSkipOnboarding = () => {
        setShowOnboardingWizard(false);
    };

    const handleOpenOnboardingWizard = () => {
        if (isFireOnboardingComplete) {
            setOnboardingStep(ONBOARDING_STEPS);
        } else {
            if (hasCompletedOnboarding) {
                setHasCompletedOnboarding(false);
            }
            setOnboardingStep(firstIncompleteOnboardingStep);
        }
        setShowOnboardingWizard(true);
    };

    const handleToggleTheme = () => {
        const nextTheme: ThemeMode = theme === 'dark' ? 'light' : 'dark';
        setTheme(nextTheme);
        persistPortfolios(
            portfolios,
            selectedCurrency,
            currentPortfolioId,
            onboardingStep,
            hasCompletedOnboarding,
            onboardingCompletedAt,
            nextTheme
        ).catch((e) => console.error('Theme Save Error', e));
    };

    useEffect(() => {
        if (hasCompletedOnboarding && isFireOnboardingComplete && onboardingStep < ONBOARDING_STEPS) {
            setOnboardingStep(ONBOARDING_STEPS);
        }
    }, [hasCompletedOnboarding, isFireOnboardingComplete, onboardingStep]);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove('theme-dark', 'theme-light');
        root.classList.add(theme === 'light' ? 'theme-light' : 'theme-dark');
    }, [theme]);

    useEffect(() => {
        if (view !== 'holdings') {
            setShowPortfolioMenu(false);
        }
    }, [view]);

    useEffect(() => {
        if (!portfolios.length) {
            if (currentPortfolioId) setCurrentPortfolioId('');
            return;
        }

        if (!portfolios.some((p) => p.id === currentPortfolioId)) {
            setCurrentPortfolioId(portfolios[0].id);
        }
    }, [portfolios, currentPortfolioId]);

    useEffect(() => {
        const raw = localStorage.getItem('fire_last_snapshot');
        if (raw) {
            try {
                setLastSnapshot(JSON.parse(raw));
            } catch {
                // ignore parse errors
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('frostball_settings_unread_notifications', String(unreadSettingsNotifications));
    }, [unreadSettingsNotifications]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleOpenNotifications = () => {
        setShowNotificationsPanel(true);
        setUnreadSettingsNotifications(0);
    };

    const handleOpenMyAccount = () => {
        setShowUserMenu(false);
        setShowMyAccountPanel(true);
        setAccountPanel('private');
    };

    const handleOpenFireSetup = () => {
        setShowUserMenu(false);
        setShowMyAccountPanel(true);
        setAccountPanel('fire-setup');
    };

    const handleFireSetupAction = (target?: FireSetupActionTarget) => {
        if (!target) return;
        if (target === 'public-profile') {
            setAccountPanel('public');
            return;
        }

        setShowMyAccountPanel(false);
        setSelectedAsset(null);
        setStartEditingHolding(false);
        if (target === 'fire') setView('fire');
        if (target === 'pensions') setView('pensions');
        if (target === 'portfolio') setView('holdings');
    };

    const handleSaveAccountSettings = async () => {
        try {
            await persistGeneralSettings(accountSettings);
        } catch (error) {
            console.error('Failed to save account settings', error);
        }
    };

    if (authLoading) {
        return (
            <div className="flex h-screen bg-[#0e0e12] items-center justify-center">
                <Loader2 className="animate-spin text-sky-500" size={32} />
            </div>
        );
    }

    if (!user && !isGuest) {
        return <Login onGuest={handleGuestAccess} theme={theme} onToggleTheme={handleToggleTheme} />;
    }

    if (!isLoaded || !fireConfigLoaded) {
        return (
            <div className="flex h-screen bg-[#0e0e12] items-center justify-center">
                <Loader2 className="animate-spin text-sky-500" size={32} />
            </div>
        );
    }

    const profileDisplayName = isGuest
        ? 'Guest User'
        : getDashboardDisplayName(
              { displayName: user?.name || user?.displayName, email: user?.email },
              accountSettings
          );
    const profileInitials = profileDisplayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join('') || 'I';
    const profilePlanLabel = isGuest ? 'Read Only' : 'Pro Plan';
    const profileMenuPlanLabel = isGuest ? 'Read only access' : 'Pro Plan';
    const headerSurfaceClasses = theme === 'light'
        ? 'bg-white border-slate-200 text-slate-900'
        : 'bg-[#13131a] border-white/5 text-slate-300';
    const headerButtonClasses = theme === 'light'
        ? 'bg-white hover:bg-sky-50 text-slate-900 border-slate-200 shadow-sm'
        : 'bg-[#1e1e26] hover:bg-[#252530] text-slate-300 border-white/5';
    const headerMutedIconClasses = theme === 'light' ? 'text-slate-600' : 'text-slate-400';
    const profileBorderClasses = theme === 'light' ? 'border-slate-200' : 'border-white/5';
    const profileNameClasses = theme === 'light' ? 'text-slate-900' : 'text-white';
    const profilePlanClasses = theme === 'light' ? 'text-slate-600' : 'text-sky-400';
    const avatarClasses = theme === 'light'
        ? 'bg-sky-700 text-white border-white'
        : 'bg-slate-900 text-white border-[#13131a]';
    const notificationBadgeBorderClasses = theme === 'light' ? 'border-white' : 'border-[#13131a]';
    const menuSurfaceClasses = theme === 'light'
        ? 'bg-white border-slate-200 text-slate-900'
        : 'bg-[#1e1e26] border-white/10 text-slate-200';
    const menuDividerClasses = theme === 'light' ? 'border-slate-200' : 'border-white/10';
    const menuItemClasses = theme === 'light'
        ? 'text-slate-900 hover:bg-sky-50'
        : 'text-slate-200 hover:bg-white/5';
    const menuMutedTextClasses = theme === 'light' ? 'text-slate-600' : 'text-sky-300';
    const menuDangerClasses = theme === 'light'
        ? 'text-rose-700 hover:bg-sky-50'
        : 'text-rose-400 hover:bg-white/5';
    const accountModalSurfaceClasses = theme === 'light'
        ? 'border-sky-100 bg-[#f8fbff] text-slate-900 shadow-sky-950/10'
        : 'border-white/10 bg-[#121219] text-slate-100';
    const accountModalDividerClasses = theme === 'light' ? 'border-sky-100' : 'border-white/10';
    const accountTabBaseClasses = theme === 'light'
        ? 'text-slate-700 hover:bg-sky-50 hover:text-sky-800'
        : 'text-slate-300 hover:bg-sky-500/10 hover:text-sky-200';
    const accountActiveTabClasses = theme === 'light'
        ? 'border-sky-700 text-sky-800'
        : 'border-sky-400 text-sky-300';
    const accountNavActiveClasses = theme === 'light'
        ? 'border-l-4 border-sky-700 bg-sky-100 text-sky-900'
        : 'border-l-4 border-sky-400 bg-sky-500/15 text-sky-100';
    const accountNavInactiveClasses = theme === 'light'
        ? 'border-l-4 border-transparent text-slate-700 hover:bg-sky-50 hover:text-sky-800'
        : 'border-l-4 border-transparent text-slate-300 hover:bg-sky-500/10 hover:text-sky-100';
    const accountNavDeleteInactiveClasses = theme === 'light'
        ? 'border-l-4 border-transparent text-rose-700 hover:bg-rose-50 hover:text-rose-800'
        : 'border-l-4 border-transparent text-rose-300 hover:bg-rose-500/10 hover:text-rose-200';
    const accountBannerClasses = theme === 'light'
        ? 'border-sky-200 bg-sky-50 text-slate-800'
        : 'border-sky-500/25 bg-sky-500/10 text-slate-200';
    const accountLabelClasses = theme === 'light' ? 'text-slate-800' : 'text-slate-200';
    const accountMutedTextClasses = theme === 'light' ? 'text-slate-600' : 'text-slate-300';
    const accountHelperTextClasses = theme === 'light' ? 'text-slate-600' : 'text-slate-400';
    const accountInputClasses = theme === 'light'
        ? 'border-slate-300 bg-white text-slate-900 placeholder:text-slate-500 focus:border-sky-700 focus:ring-2 focus:ring-sky-100'
        : 'border-white/15 bg-slate-950/40 text-slate-100 placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20';
    const accountPanelCardClasses = theme === 'light'
        ? 'border-sky-100 bg-white text-slate-900'
        : 'border-white/10 bg-white/5 text-slate-100';
    const showDevGlobalDividendCalendarRefresh = canShowGlobalDividendCalendarRefresh(user);

    return (
        <div className="flex h-screen bg-[#0e0e12] font-sans text-slate-300">
            {isSidebarOpen && <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
            <Sidebar
                currentView={view}
                setView={(v) => {
                    setView(v);
                    setSelectedAsset(null);
                    setStartEditingHolding(false);
                }}
                onLogout={handleLogout}
                isMobileOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
            />

            <main className="flex-1 flex flex-col h-full overflow-hidden relative">
                {showOnboardingWizard && (
                    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
                        <div className="w-full max-w-5xl mt-6 max-h-[90vh] overflow-y-auto">
                            <OnboardingWizard
                                currentStep={onboardingStep}
                                totalSteps={ONBOARDING_STEPS}
                                onAdvance={handleAdvanceOnboarding}
                                onComplete={handleCompleteOnboarding}
                                onSkip={handleSkipOnboarding}
                                isComplete={isFireOnboardingComplete}
                                onClose={() => setShowOnboardingWizard(false)}
                                config={fireConfig}
                                onUpdateConfig={handleUpdateFireConfig}
                                currencySymbol={currencySymbol}
                                portfolioValue={totalValue}
                            />
                        </div>
                    </div>
                )}
                <header className={`h-20 border-b flex items-center justify-between px-8 flex-shrink-0 z-20 ${headerSurfaceClasses}`}>
                    <div className="flex items-center space-x-3">
                        <button
                            className={`md:hidden p-2 rounded-lg border transition-colors ${headerButtonClasses}`}
                            onClick={() => setIsSidebarOpen(true)}
                            aria-label="Open navigation"
                        >
                            <Menu size={18} />
                        </button>
                    </div>

                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleToggleTheme}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${headerButtonClasses}`}
                        >
                            {theme === 'dark' ? (
                                <SunMedium size={16} className="text-amber-400" />
                            ) : (
                                <Moon size={16} className={headerMutedIconClasses} />
                            )}
                            <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => setShowCurrencyMenu(!showCurrencyMenu)}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${headerButtonClasses}`}
                            >
                                <Globe size={16} className={headerMutedIconClasses} />
                                <span>{selectedCurrency}</span>
                                <ChevronDown size={14} className={headerMutedIconClasses} />
                            </button>

                            {showCurrencyMenu && (
                                <div className={`absolute right-0 mt-2 w-32 rounded-xl border shadow-xl z-50 overflow-hidden ${menuSurfaceClasses}`}>
                                    {(['GBP', 'USD', 'EUR'] as SupportedCurrency[]).map((curr) => (
                                        <button
                                            key={curr}
                                            onClick={() => {
                                                setSelectedCurrency(curr);
                                                setShowCurrencyMenu(false);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm flex justify-between items-center ${menuItemClasses}`}
                                        >
                                            <span>{curr}</span>
                                            {selectedCurrency === curr && <Check size={14} className="text-sky-400" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className={`flex items-center space-x-3 border-l pl-6 ${profileBorderClasses}`}>
                            <div className="text-right hidden sm:block">
                                <div className={`text-sm font-semibold ${profileNameClasses}`}>{profileDisplayName}</div>
                                <div className={`text-xs ${profilePlanClasses}`}>{profilePlanLabel}</div>
                            </div>
                            <div className="relative" ref={userMenuRef}>
                                <button
                                    onClick={() => setShowUserMenu((prev) => !prev)}
                                    className="relative flex h-9 w-9 items-center justify-center rounded-full p-[2px] focus:outline-none focus:ring-2 focus:ring-sky-400 focus:ring-offset-2 focus:ring-offset-transparent"
                                    aria-label="Open settings menu"
                                >
                                    {user?.photoUrl && !isGuest ? (
                                        <img
                                            src={user.photoUrl}
                                            alt="User"
                                            className={`h-full w-full rounded-full border-2 object-cover ${avatarClasses}`}
                                        />
                                    ) : (
                                        <span className={`flex h-full w-full items-center justify-center rounded-full border-2 text-sm font-bold ${avatarClasses}`}>
                                            {profileInitials}
                                        </span>
                                    )}
                                    {unreadSettingsNotifications > 0 && (
                                        <span className={`absolute -right-1 -top-1 min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-semibold flex items-center justify-center border ${notificationBadgeBorderClasses}`}>
                                            {unreadSettingsNotifications}
                                        </span>
                                    )}
                                </button>

                                {showUserMenu && (
                                    <div className={`absolute right-0 mt-2 w-72 rounded-xl border shadow-xl z-50 overflow-hidden ${menuSurfaceClasses}`}>
                                        <div className={`px-4 py-3 border-b ${menuDividerClasses}`}>
                                            <div className={`text-sm font-semibold ${profileNameClasses}`}>{profileDisplayName}</div>
                                            <div className={`text-xs mt-1 ${menuMutedTextClasses}`}>{profileMenuPlanLabel}</div>
                                        </div>

                                        <div className="py-2">
                                            <button
                                                onClick={handleOpenMyAccount}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${menuItemClasses}`}
                                            >
                                                <User size={14} className={headerMutedIconClasses} />
                                                <span>My account</span>
                                            </button>
                                            <button className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${menuItemClasses}`}>
                                                <Crown size={14} className="text-amber-400" />
                                                <span>My subscription</span>
                                            </button>
                                            <button
                                                onClick={handleOpenNotifications}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 ${menuItemClasses}`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Bell size={14} className={headerMutedIconClasses} />
                                                    <span>Notifications</span>
                                                </span>
                                                {unreadSettingsNotifications > 0 && (
                                                    <span className="min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[11px] font-semibold flex items-center justify-center">
                                                        {unreadSettingsNotifications}
                                                    </span>
                                                )}
                                            </button>
                                        </div>

                                        <div className={`py-2 border-t ${menuDividerClasses}`}>
                                            <button className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${menuItemClasses}`}>
                                                <CircleHelp size={14} className={headerMutedIconClasses} />
                                                <span>Help</span>
                                            </button>
                                            <button className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${menuItemClasses}`}>
                                                <Sparkles size={14} className="text-sky-400" />
                                                <span>What's new</span>
                                            </button>
                                        </div>

                                        <div className={`py-2 border-t ${menuDividerClasses}`}>
                                            <button
                                                onClick={handleToggleTheme}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-2 ${menuItemClasses}`}
                                            >
                                                <span className="flex items-center gap-2">
                                                    <Settings size={14} className={headerMutedIconClasses} />
                                                    <span>Appearance ({theme === 'dark' ? 'Dark' : 'Light'})</span>
                                                </span>
                                                {theme === 'dark' ? (
                                                    <SunMedium size={14} className="text-amber-400" />
                                                ) : (
                                                    <Moon size={14} className={headerMutedIconClasses} />
                                                )}
                                            </button>
                                        </div>

                                        <div className={`py-2 border-t ${menuDividerClasses}`}>
                                            <button
                                                onClick={handleLogout}
                                                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ${menuDangerClasses}`}
                                            >
                                                <LogOut size={14} />
                                                <span>Logout</span>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {showNotificationsPanel && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                        <div className="w-full max-w-md rounded-2xl bg-[#1e1e26] border border-white/10 shadow-2xl overflow-hidden">
                            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                                <h3 className="text-white font-semibold flex items-center gap-2">
                                    <Bell size={16} className="text-rose-400" />
                                    Notifications
                                </h3>
                                <button
                                    onClick={() => setShowNotificationsPanel(false)}
                                    className="text-slate-400 hover:text-white text-sm"
                                >
                                    Close
                                </button>
                            </div>
                            <div className="px-5 py-4 space-y-3">
                                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2">
                                    <p className="text-sm text-rose-100 font-medium">Push updates enabled</p>
                                    <p className="text-xs text-rose-200/80 mt-1">
                                        We&apos;ll keep sending important push notifications here as new messages are published.
                                    </p>
                                </div>
                                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                                    <p className="text-sm text-white">Welcome to settings notifications.</p>
                                    <p className="text-xs text-slate-400 mt-1">No unread messages right now.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {showMyAccountPanel && (
                    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 sm:p-8 overflow-y-auto">
                        <div className={`mx-auto w-full max-w-6xl overflow-hidden rounded-2xl border shadow-2xl ${accountModalSurfaceClasses}`}>
                            <div className={`flex items-center justify-between border-b px-6 py-4 ${accountModalDividerClasses}`}>
                                <div className="flex flex-wrap items-center gap-2 text-sm font-medium">
                                    <button className={`rounded-t-md border-b-2 px-2 py-1 ${accountActiveTabClasses}`}>Account</button>
                                    <button className={`rounded-md px-2 py-1 transition-colors ${accountTabBaseClasses}`}>Subscription settings</button>
                                    <button className={`rounded-md px-2 py-1 transition-colors ${accountTabBaseClasses}`}>Display settings</button>
                                    <button className={`rounded-md px-2 py-1 transition-colors ${accountTabBaseClasses}`}>Invite a friend</button>
                                    <button className={`rounded-md px-2 py-1 transition-colors ${accountTabBaseClasses}`}>Partner program</button>
                                </div>
                                <button
                                    onClick={() => setShowMyAccountPanel(false)}
                                    className={`rounded-lg p-2 transition-colors ${theme === 'light' ? 'text-slate-700 hover:bg-sky-100 hover:text-sky-900' : 'text-slate-300 hover:bg-sky-500/10 hover:text-white'}`}
                                    aria-label="Close my account settings"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-[230px_minmax(0,1fr)] gap-6 p-6">
                                <div className="space-y-2">
                                    {[
                                        { id: 'private', label: 'Private details' },
                                        { id: 'public', label: 'Public profile' },
                                        { id: 'fire-setup', label: 'FIRE Setup' },
                                        ...(showDevGlobalDividendCalendarRefresh ? [{ id: 'developer', label: 'Developer tools' }] : []),
                                        { id: 'security', label: 'Security' },
                                        { id: 'notifications', label: 'Notifications' },
                                        { id: 'delete', label: 'Delete my account' },
                                    ].map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => setAccountPanel(item.id as AccountPanel)}
                                            className={`w-full rounded-lg px-4 py-3 text-left text-sm transition-colors ${
                                                accountPanel === item.id
                                                    ? accountNavActiveClasses
                                                    : item.id === 'delete' ? accountNavDeleteInactiveClasses : accountNavInactiveClasses
                                            }`}
                                        >
                                            {item.label}
                                        </button>
                                    ))}
                                </div>

                                <div className="space-y-4">
                                    {accountPanel === 'private' && (
                                        <>
                                            <div className={`rounded-lg border px-4 py-3 text-sm ${accountBannerClasses}`}>
                                                🔒 This is your confidential information, it is not available to other users.
                                            </div>
                                            <label className={`block text-sm font-medium ${accountLabelClasses}`}>
                                                Display nickname
                                                <input
                                                    value={accountSettings.displayName}
                                                    onChange={(event) =>
                                                        setAccountSettings((prev) => ({ ...prev, displayName: event.target.value }))
                                                    }
                                                    placeholder="Investor"
                                                    className={`mt-2 w-full rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                />
                                                <span className={`mt-1 block text-xs ${accountHelperTextClasses}`}>Used in your dashboard greeting. Choose a nickname instead of personal details.</span>
                                            </label>
                                            <label className={`block text-sm font-medium ${accountLabelClasses}`}>
                                                Email
                                                <input
                                                    value={accountSettings.email}
                                                    onChange={(event) =>
                                                        setAccountSettings((prev) => ({ ...prev, email: event.target.value }))
                                                    }
                                                    className={`mt-2 w-full rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                />
                                            </label>
                                            <label className={`block text-sm font-medium ${accountLabelClasses}`}>
                                                Full name
                                                <input
                                                    value={accountSettings.fullName}
                                                    onChange={(event) =>
                                                        setAccountSettings((prev) => ({ ...prev, fullName: event.target.value }))
                                                    }
                                                    className={`mt-2 w-full rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                />
                                            </label>
                                        </>
                                    )}

                                    {accountPanel === 'public' && (
                                        <>
                                            <div className={`rounded-lg border px-4 py-3 text-sm ${accountBannerClasses}`}>
                                                🌐 This is your public information, other users can see it on your profile and comments.
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <label className={`block text-sm font-medium ${accountLabelClasses}`}>
                                                    Username
                                                    <input
                                                        value={accountSettings.username}
                                                        onChange={(event) =>
                                                            setAccountSettings((prev) => ({ ...prev, username: event.target.value }))
                                                        }
                                                        className={`mt-2 w-full rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                    />
                                                </label>
                                                <label className={`block text-sm font-medium ${accountLabelClasses}`}>
                                                    Name / Blog name
                                                    <input
                                                        value={accountSettings.blogName}
                                                        onChange={(event) =>
                                                            setAccountSettings((prev) => ({ ...prev, blogName: event.target.value }))
                                                        }
                                                        className={`mt-2 w-full rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                    />
                                                </label>
                                            </div>
                                            <div className="space-y-3">
                                                <p className={`text-sm ${accountMutedTextClasses}`}>Links to your social networks/blogs</p>
                                                {accountSettings.socialLinks.map((link, index) => (
                                                    <div key={index} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        <input
                                                            value={link.label}
                                                            onChange={(event) =>
                                                                setAccountSettings((prev) => ({
                                                                    ...prev,
                                                                    socialLinks: prev.socialLinks.map((item, idx) =>
                                                                        idx === index ? { ...item, label: event.target.value } : item
                                                                    ),
                                                                }))
                                                            }
                                                            placeholder="Name (twitter, youtube, ...)"
                                                            className={`rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                        />
                                                        <input
                                                            value={link.url}
                                                            onChange={(event) =>
                                                                setAccountSettings((prev) => ({
                                                                    ...prev,
                                                                    socialLinks: prev.socialLinks.map((item, idx) =>
                                                                        idx === index ? { ...item, url: event.target.value } : item
                                                                    ),
                                                                }))
                                                            }
                                                            placeholder="https://"
                                                            className={`rounded-lg border px-3 py-2 focus:outline-none ${accountInputClasses}`}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    {accountPanel === 'fire-setup' && (
                                        <div className="space-y-5">
                                            <div className={`rounded-2xl border p-5 ${accountPanelCardClasses}`}>
                                                <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                                                    <div>
                                                        <h3 className={`text-2xl font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-slate-100'}`}>FIRE Setup</h3>
                                                        <p className={`mt-2 text-sm ${accountMutedTextClasses}`}>
                                                            Track the setup steps needed for accurate FIRE, portfolio and income analysis.
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <div
                                                            className="grid h-20 w-20 place-items-center rounded-full"
                                                            style={{
                                                                background: `conic-gradient(#0EA5E9 ${fireSetupCompletion.percent * 3.6}deg, ${theme === 'light' ? '#E2E8F0' : '#1F2937'} 0deg)`,
                                                            }}
                                                            aria-label={`FIRE Setup is ${fireSetupCompletion.percent}% complete`}
                                                        >
                                                            <div className={`grid h-14 w-14 place-items-center rounded-full text-sm font-bold ${theme === 'light' ? 'bg-white text-sky-800' : 'bg-[#121219] text-sky-200'}`}>
                                                                {fireSetupCompletion.percent}%
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <div className={`text-2xl font-bold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>{fireSetupCompletion.percent}% complete</div>
                                                            <div className={`text-sm ${accountMutedTextClasses}`}>
                                                                {fireSetupCompletion.completedCount} of {fireSetupCompletion.totalCount} setup steps complete
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                {fireSetupCompletion.steps.map((step) => (
                                                    <div
                                                        key={step.id}
                                                        className={`flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between ${
                                                            theme === 'light'
                                                                ? 'border-slate-200 bg-white text-slate-900'
                                                                : 'border-white/10 bg-white/[0.04] text-slate-100'
                                                        }`}
                                                    >
                                                        <div className="flex items-start gap-3">
                                                            <div
                                                                className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border ${
                                                                    step.completed
                                                                        ? 'border-emerald-500 bg-emerald-500 text-white'
                                                                        : theme === 'light'
                                                                            ? 'border-sky-300 bg-sky-50 text-sky-700'
                                                                            : 'border-sky-400/60 bg-sky-500/10 text-sky-200'
                                                                }`}
                                                            >
                                                                {step.completed ? <Check size={16} strokeWidth={3} /> : <span className="h-2.5 w-2.5 rounded-full bg-current" />}
                                                            </div>
                                                            <div>
                                                                <div className="font-semibold">{step.label}</div>
                                                                <div className={`mt-1 text-sm ${step.completed ? (theme === 'light' ? 'text-emerald-700' : 'text-emerald-300') : accountMutedTextClasses}`}>
                                                                    {step.completed ? 'Complete' : 'Incomplete — update this setup item for better analysis.'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {step.actionLabel && (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleFireSetupAction(step.actionTarget)}
                                                                className={theme === 'light'
                                                                    ? 'rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 hover:bg-sky-100'
                                                                    : 'rounded-lg border border-sky-400/30 bg-sky-500/10 px-3 py-2 text-sm font-semibold text-sky-100 hover:bg-sky-500/20'}
                                                            >
                                                                {step.actionLabel}
                                                            </button>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {accountPanel === 'developer' && showDevGlobalDividendCalendarRefresh && (
                                        <div className="space-y-4">
                                            <DevGlobalDividendCalendarRefresh
                                                user={user}
                                                theme={theme}
                                                endpointBase={devGlobalDividendCalendarRefreshEndpointBase}
                                            />
                                        </div>
                                    )}

                                    {accountPanel === 'security' && (
                                        <div className={`rounded-lg border p-5 space-y-3 ${accountPanelCardClasses}`}>
                                            <h3 className={`text-xl font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-slate-100'}`}>Two-Factor Authentication (2FA)</h3>
                                            <p className={`text-sm ${accountMutedTextClasses}`}>Enable this in your identity provider (Google, Apple, etc.) for stronger login security.</p>
                                            <button className="rounded-lg bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-600">Change password</button>
                                        </div>
                                    )}

                                    {accountPanel === 'notifications' && (
                                        <div className="space-y-3">
                                            <label className={`flex items-center gap-3 ${accountLabelClasses}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={accountSettings.receiveEmailNotifications}
                                                    onChange={(event) =>
                                                        setAccountSettings((prev) => ({
                                                            ...prev,
                                                            receiveEmailNotifications: event.target.checked,
                                                        }))
                                                    }
                                                    className={theme === 'light' ? 'h-4 w-4 rounded border-slate-300 bg-white text-sky-700' : 'h-4 w-4 rounded border-white/20 bg-white/5 text-sky-400'}
                                                />
                                                Receive email notifications
                                            </label>
                                            <label className={`flex items-center gap-3 ${accountLabelClasses}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={accountSettings.receiveBlogDigest}
                                                    onChange={(event) =>
                                                        setAccountSettings((prev) => ({ ...prev, receiveBlogDigest: event.target.checked }))
                                                    }
                                                    className={theme === 'light' ? 'h-4 w-4 rounded border-slate-300 bg-white text-sky-700' : 'h-4 w-4 rounded border-white/20 bg-white/5 text-sky-400'}
                                                />
                                                Receive blog digest
                                            </label>
                                        </div>
                                    )}

                                    {accountPanel === 'delete' && (
                                        <div className="space-y-3">
                                            <h3 className={`text-2xl font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-slate-100'}`}>Delete my account</h3>
                                            <p className={accountMutedTextClasses}>Delete the account and all profiles.</p>
                                            <button className={theme === 'light' ? 'rounded-lg bg-rose-100 px-4 py-2 font-medium text-rose-700 hover:bg-rose-200' : 'rounded-lg bg-rose-500/20 px-4 py-2 font-medium text-rose-200 hover:bg-rose-500/30'}>Delete</button>
                                        </div>
                                    )}

                                    <div className="flex justify-end pt-4">
                                        <button
                                            onClick={handleSaveAccountSettings}
                                            className="rounded-lg bg-sky-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm shadow-sky-900/20 hover:bg-sky-600"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-8 custom-scrollbar bg-[#0e0e12]">
                    <div className="max-w-7xl mx-auto space-y-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div>
                                <h1 className="text-2xl font-bold text-white tracking-tight">
                                    {view === 'dashboard'
                                        ? `Welcome, ${welcomeName}`
                                        : view === 'holdings'
                                        ? selectedAsset
                                            ? 'Asset Details'
                                            : 'Holdings'
                                        : view === 'ai-lab'
                                        ? 'AI Laboratory'
                                        : view === 'fire'
                                        ? 'FIRE Command Center'
                                        : view === 'pensions'
                                        ? 'Pensions Dashboard'
                                        : 'Analytics'}
                                </h1>
                                {view === 'dashboard' && (
                                    <p className="text-sm text-slate-400 mt-1">
                                        Your FIRE journey, portfolio performance, dividends and AI analyst signals.
                                    </p>
                                )}
                                {view === 'holdings' && (
                                    <div className="text-slate-500 text-sm mt-1 flex flex-wrap items-center gap-2">
                                        {isEditingPortfolioName ? (
                                            <div className="flex items-center gap-2">
                                                <input
                                                    value={portfolioNameDraft}
                                                    onChange={(event) => setPortfolioNameDraft(event.target.value)}
                                                    className="bg-[#1e1e26] border border-sky-500/30 text-xs text-sky-200 rounded px-2 py-1 focus:outline-none focus:border-sky-500/70"
                                                    aria-label="Portfolio name"
                                                />
                                                <button
                                                    onClick={handleSavePortfolioName}
                                                    className="text-xs text-sky-300 hover:text-sky-200"
                                                >
                                                    Save
                                                </button>
                                                <button
                                                    onClick={() => {
                                                        setIsEditingPortfolioName(false);
                                                        setPortfolioNameDraft(currentPortfolioName);
                                                    }}
                                                    className="text-xs text-slate-400 hover:text-slate-200"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <span>
                                                    {currentTransactions.length > 0
                                                        ? `Last updated: Just now · ${currentTransactions.length} txns`
                                                        : 'No transactions yet. Import data to start.'}
                                                </span>
                                                <button
                                                    onClick={() => setIsEditingPortfolioName(true)}
                                                    className="text-sky-400 hover:text-sky-300"
                                                    title="Edit portfolio name"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                {currentPortfolio && (
                                                    <button
                                                        onClick={handleDeletePortfolio}
                                                        className="text-rose-400 hover:text-rose-300"
                                                        title="Delete portfolio"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        <div className="flex items-center flex-wrap gap-3 justify-end w-full sm:w-auto">
                            {view === 'holdings' && (
                                <>
                                    <div className="relative">
                                        <button
                                            onClick={() => setShowPortfolioMenu(!showPortfolioMenu)}
                                            className="flex items-center space-x-2 bg-[#1e1e26] hover:bg-[#252530] text-white px-4 py-2 rounded-lg text-sm font-medium border border-white/5 transition-colors shadow-lg"
                                        >
                                            <Wallet size={16} className="text-sky-400" />
                                            <span>{currentPortfolioName}</span>
                                            <ChevronDown size={14} className="text-slate-500" />
                                        </button>

                                        {showPortfolioMenu && (
                                            <div className="absolute right-0 mt-2 w-64 bg-[#1e1e26] border border-white/10 rounded-xl shadow-xl z-50 overflow-hidden">
                                                <div className="py-2">
                                                    <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Switch Portfolio</div>
                                                    {portfolios.map((p) => (
                                                        <button
                                                            key={p.id}
                                                            onClick={() => {
                                                                setCurrentPortfolioId(p.id);
                                                                setShowPortfolioMenu(false);
                                                            }}
                                                            className={`w-full text-left px-4 py-3 text-sm flex justify-between items-center ${menuItemClasses}`}
                                                        >
                                                            <span className="truncate">{getPortfolioDisplayName(p)}</span>
                                                            {currentPortfolioId === p.id && <Check size={14} className="text-sky-400" />}
                                                        </button>
                                                    ))}
                                                </div>
                                            <div className="border-t border-white/10 p-2">
                                                <button
                                                    onClick={handleCreatePortfolio}
                                                    className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-sky-600/10 hover:bg-sky-600/20 text-sky-400 rounded-lg text-sm font-medium transition-colors"
                                                >
                                                    <Plus size={14} />
                                                    <span>New Portfolio</span>
                                                </button>
                                                {currentPortfolio && (
                                                    <button
                                                        onClick={handleDeletePortfolio}
                                                        className="mt-2 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 rounded-lg text-sm font-medium transition-colors"
                                                    >
                                                        <Trash2 size={14} />
                                                        <span>Delete Portfolio</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                    <button
                                        onClick={handleCreatePortfolio}
                                        className="flex items-center space-x-2 bg-sky-600/10 hover:bg-sky-600/20 text-sky-300 px-4 py-2 rounded-lg text-sm font-medium border border-sky-500/30 transition-colors"
                                    >
                                        <Plus size={14} />
                                        <span className="hidden sm:inline">New Portfolio</span>
                                        <span className="sm:hidden">Add</span>
                                    </button>
                                </>
                            )}

                            {view === 'holdings' && currentTransactions.length > 0 && (
                                <button
                                    onClick={handleClearPortfolio}
                                    className="p-2 text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors"
                                    title="Clear current portfolio data"
                                >
                                    <Trash2 size={18} />
                                </button>
                            )}

                            {showImportActions && (
                                <>

                                    <button
                                        onClick={() => setShowUploadModal(true)}
                                        className="px-4 py-2 bg-[#12121a] border border-sky-500/40 text-sky-100 rounded-lg text-sm font-medium transition-colors flex items-center shadow-lg hover:border-sky-400 hover:bg-sky-500/10 [html.theme-light_&]:bg-sky-50 [html.theme-light_&]:text-sky-800 [html.theme-light_&]:border-sky-500/50 [html.theme-light_&]:hover:bg-sky-100"
                                    >
                                        <Sparkles size={16} className="mr-2" /> Smart import (AI)
                                    </button>

                                    <button
                                        onClick={() => {
                                            setTransactionModalDraft(null);
                                            setShowTransactionModal(true);
                                        }}
                                        className="px-4 py-2 bg-sky-600 hover:bg-sky-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center shadow-lg shadow-sky-500/20"
                                    >
                                        <Plus size={18} className="mr-2" /> Add Trade/Dividends
                                    </button>

                                </>
                            )}
                        </div>
                    </div>

                        {view === 'holdings' && saveState.status !== 'idle' && (
                            <div className="mt-2 space-y-3">
                                <div
                                    className={`rounded-xl border p-4 text-sm ${
                                        saveState.status === 'error'
                                            ? 'border-rose-500/30 bg-rose-500/10 ' +
                                              (theme === 'light' ? 'text-rose-900' : 'text-rose-100')
                                            : saveState.status === 'success'
                                            ? 'border-emerald-500/30 bg-emerald-500/10 ' +
                                              (theme === 'light' ? 'text-emerald-900' : 'text-emerald-100')
                                            : 'border-sky-500/30 bg-sky-500/10 ' +
                                              (theme === 'light' ? 'text-sky-900' : 'text-sky-100')
                                    }`}
                                >
                                    {saveState.message}
                                </div>

                                {(saveState.status === 'pending' ||
                                    saveState.status === 'error' ||
                                    saveState.status === 'saving') && (
                                    <button
                                        onClick={handleSaveImportedPortfolios}
                                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-sm font-medium transition-colors flex items-center shadow-lg shadow-emerald-500/20"
                                        disabled={saveState.status === 'saving'}
                                    >
                                        <Download size={16} className="mr-2" />
                                        {saveState.status === 'saving' ? 'Saving…' : 'Save imports'}
                                    </button>
                                )}
                            </div>
                        )}

                        {view === 'dashboard' && (
                            <div className="space-y-6">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                    <div>
                                        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-300 [html.theme-light_&]:text-sky-800">EXECUTIVE SUMMARY</p>
                                    </div>
                                </div>

                                <div className={theme === 'light'
                                    ? 'flex flex-col gap-3 rounded-xl border border-sky-100 bg-white px-4 py-3 text-slate-900 shadow-sm sm:flex-row sm:items-center sm:justify-between'
                                    : 'flex flex-col gap-3 rounded-xl border border-sky-500/20 bg-sky-500/10 px-4 py-3 text-slate-100 sm:flex-row sm:items-center sm:justify-between'}>
                                    <div className="flex items-center gap-3">
                                        <div className={theme === 'light' ? 'flex h-10 w-10 items-center justify-center rounded-full bg-sky-100 text-sky-800' : 'flex h-10 w-10 items-center justify-center rounded-full bg-sky-400/15 text-sky-200'}>
                                            <Flame size={18} />
                                        </div>
                                        <div>
                                            <div className="font-semibold">FIRE Setup</div>
                                            <div className={theme === 'light' ? 'text-sm text-slate-700' : 'text-sm text-slate-300'}>{fireSetupCompletion.percent}% complete</div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleOpenFireSetup}
                                        className={theme === 'light'
                                            ? 'self-start rounded-lg bg-sky-700 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-600 sm:self-auto'
                                            : 'self-start rounded-lg bg-sky-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-sky-400 sm:self-auto'}
                                    >
                                        {fireSetupCompletion.isComplete ? 'Edit setup' : 'Finish setup'}
                                    </button>
                                </div>

                                {hasCompletedOnboarding && !hasFireAssets && (
                                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-200">
                                        Add assets to unlock accurate FIRE progress.
                                    </div>
                                )}

                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                                    <MetricCard
                                        title="FIRE Progress"
                                        value={hasFireAssets ? `${fireProgress.toFixed(1)}%` : '—'}
                                        subValue={hasFireAssets ? `FIRE gap ${formatCompactCurrency(Math.max(fireTarget - currentNetWorth, 0))}` : 'Add assets to unlock progress'}
                                        secondaryValue={hasFireAssets ? `Target ${formatCompactCurrency(fireTarget)}` : undefined}
                                        icon={<Flame size={18} />}
                                        tooltip={hasFireAssets ? fireTargetTooltip : 'Add assets to unlock accurate FIRE progress.'}
                                        accentColor="#38BDF8"
                                        trend={hasFireAssets ? (netWorthGap >= 0 ? 'up' : 'down') : 'neutral'}
                                        showSparkline={hasFireAssets}
                                        sparklineDirection={hasFireAssets ? (netWorthGap >= 0 ? 'up' : 'down') : 'up'}
                                        sparklineColor={hasFireAssets ? (netWorthGap >= 0 ? '#34d399' : '#fb7185') : '#38BDF8'}
                                    />
                                    <MetricCard
                                        title="Net Worth"
                                        value={formatCompactCurrency(currentNetWorth)}
                                        changeValueLabel={`${netWorthChangeSinceStart >= 0 ? '+' : '-'}${formatCompactCurrency(Math.abs(netWorthChangeSinceStart))}`}
                                        change={hasNetWorthBaseline ? netWorthChangeSinceStartPct : 0}
                                        periodLabel={hasNetWorthBaseline ? 'Since start' : 'No baseline'}
                                        trend={netWorthChangeSinceStart >= 0 ? 'up' : 'down'}
                                        tooltip="Assets minus liabilities across your FIRE plan."
                                        accentColor="#0EA5E9"
                                        showSparkline
                                        stackedBarSegments={netWorthAssetSegments}
                                        currencySymbol={currencySymbol}
                                        sparklineDirection={netWorthChangeSinceStart >= 0 ? 'up' : 'down'}
                                        sparklineColor={netWorthChangeSinceStart >= 0 ? '#34d399' : '#fb7185'}
                                    />
                                    <MetricCard
                                        title="Portfolio Value"
                                        value={formatCompactCurrency(totalValue)}
                                        subValue={`${formatCompactCurrency(totalInvested)} invested`}
                                        icon={<Wallet size={18} />}
                                        tooltip="Current market value of your holdings compared to your invested capital."
                                        accentColor="#38BDF8"
                                    />
                                    <MetricCard
                                        title="Dividend Income"
                                        value={dashboardDividendIncomeKpi.value}
                                        subValue={dashboardDividendIncomeKpi.subValue}
                                        secondaryValue={dashboardDividendIncomeKpi.secondaryValue}
                                        icon={<HandCoins size={18} />}
                                        tooltip={dashboardDividendIncomeKpi.tooltip}
                                        accentColor="#38BDF8"
                                    />
                                </div>

                                <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                                    <div className="min-w-0 rounded-2xl border border-sky-500/20 bg-[#1e1e26] p-5 shadow-lg xl:col-span-2">
                                        <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                                            <div className="min-w-0">
                                                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-300 [html.theme-light_&]:text-sky-800">FIRE Journey</p>
                                                <h3 className="mt-2 text-xl font-bold text-white">Path to financial independence</h3>
                                                <p className="mt-1 text-sm text-slate-400">Progress, gap and target trajectory without duplicating portfolio detail.</p>
                                            </div>
                                            <div className="grid min-w-0 grid-cols-2 gap-3 text-sm sm:grid-cols-4 lg:max-w-2xl">
                                                <div className="rounded-xl bg-white/[0.03] px-3 py-2">
                                                    <div className="text-xs text-slate-500">Progress</div>
                                                    <div className="whitespace-nowrap text-lg font-bold leading-tight text-sky-200">{fireProgress.toFixed(1)}%</div>
                                                </div>
                                                <div className="rounded-xl bg-white/[0.03] px-3 py-2">
                                                    <div className="text-xs text-slate-500">Net worth</div>
                                                    <div className="whitespace-nowrap text-lg font-bold leading-tight text-white">{formatCompactCurrency(currentNetWorth)}</div>
                                                </div>
                                                <div className="rounded-xl bg-white/[0.03] px-3 py-2">
                                                    <div className="text-xs text-slate-500">FIRE target</div>
                                                    <div className="whitespace-nowrap text-lg font-bold leading-tight text-white">{formatCompactCurrency(fireTarget)}</div>
                                                </div>
                                                <div className="rounded-xl bg-white/[0.03] px-3 py-2">
                                                    <div className="text-xs text-slate-500">Gap</div>
                                                    <div className="whitespace-nowrap text-lg font-bold leading-tight text-amber-300">{formatCompactCurrency(Math.max(fireTarget - currentNetWorth, 0))}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <FireJourneyChart currentAge={fireConfig.currentAge} retirementAge={fireConfig.retirementAge} currentNetWorth={currentNetWorth} fireTarget={fireTarget} currencySymbol={currencySymbol} snapshots={fireConfig.snapshots} />
                                        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                                            <span>Target age {fireConfig.retirementAge}</span>
                                            <span className="h-1 w-1 rounded-full bg-slate-600" />
                                            <span>{journeyDurationLabel}</span>
                                            {netWorthGap < 0 ? (
                                                <span className="text-amber-300">{formatCompactCurrency(netWorthGapAbs)} behind required path</span>
                                            ) : (
                                                <span className="text-emerald-400">{formatCompactCurrency(netWorthGapAbs)} ahead of schedule</span>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="min-w-0 rounded-2xl border border-white/5 bg-[#1e1e26] p-5 text-left shadow-lg transition-colors hover:border-purple-400/40 [html.theme-light_&]:bg-white"
                                        onClick={() => setView('ai-lab')}
                                    >
                                        <div className="mb-4 flex items-center justify-between gap-3">
                                            <div className="flex min-w-0 items-center gap-2 text-purple-300 [html.theme-light_&]:text-purple-700">
                                                <Sparkles size={18} className="flex-shrink-0" />
                                                <span className="truncate text-xs font-bold uppercase tracking-[0.2em]">AI Analyst</span>
                                            </div>
                                            <span className="rounded-full bg-purple-500/10 px-2.5 py-1 text-xs font-semibold text-purple-300 [html.theme-light_&]:text-purple-700">AI signal</span>
                                        </div>
                                        <h3 className="text-xl font-bold text-white">AI Portfolio Health Check</h3>
                                        <p className="mt-1 text-sm text-slate-400">Analysed {assets.length} assets</p>
                                        <ul className="mt-5 space-y-3">
                                            {aiInsightBullets.slice(0, 3).map((insight, index) => (
                                                <li key={`${insight}-${index}`} className="flex gap-2 text-sm text-slate-300 [html.theme-light_&]:text-slate-700">
                                                    <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-purple-300" />
                                                    <span className="line-clamp-2">{insight}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="mt-5 inline-flex items-center rounded-xl bg-purple-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-purple-500">
                                            Review analysis →
                                        </div>
                                    </button>
                                </div>

                                <section className="space-y-4">
                                    <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold text-white">Portfolio snapshot</h3>
                                            <p className="text-sm text-slate-400">Compact performance and allocation summary. Full holdings stay on the Portfolio page.</p>
                                        </div>
                                        <button onClick={() => setView('holdings')} className="text-sm font-semibold text-sky-300 hover:text-sky-200">View full portfolio →</button>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:col-span-2">
                                            <MetricCard title="Total return" value={`${totalProfit >= 0 ? '+' : '-'}${currencySymbol}${Math.abs(totalProfit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} change={totalReturnPct} periodLabel="All time" icon={<LineChart size={18} />} trend={totalProfit >= 0 ? 'up' : 'down'} tooltip="Overall gains or losses across your portfolio." accentColor={totalProfit >= 0 ? '#22c55e' : '#fb7185'} />
                                            <MetricCard title="Today’s P/L" value={`${dailyHoldingsStats.totalDailyPnL >= 0 ? '+' : '-'}${currencySymbol}${Math.abs(dailyHoldingsStats.totalDailyPnL).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} change={dailyHoldingsStats.totalDailyPct} changeLabel={todayPnLDateLabel} icon={<Activity size={18} />} trend={dailyHoldingsStats.totalDailyPnL >= 0 ? 'up' : 'down'} tooltip="Daily change since the previous close using current, non-stale quotes." accentColor={dailyHoldingsStats.totalDailyPnL >= 0 ? '#22c55e' : '#fb7185'} />

                                            <div className="rounded-2xl border border-white/5 bg-[#1e1e26] p-6 shadow-lg md:col-span-2">
                                                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                                                    <div>
                                                        <h4 className="font-bold text-slate-100">Top movers today</h4>
                                                        <p className="mt-1 text-xs text-slate-500">Sorted by daily {currencySymbol} impact from current holdings.</p>
                                                    </div>
                                                    {topMoversQuoteTimeLabel && <span className="text-xs text-slate-500">as of {topMoversQuoteTimeLabel}</span>}
                                                </div>
                                                {topMovers.gainers.length === 0 && topMovers.losers.length === 0 ? (
                                                    <div className="rounded-xl border border-dashed border-white/10 bg-[#15151c] px-4 py-8 text-center text-sm text-slate-500">No daily movers yet — waiting on current quotes.</div>
                                                ) : (
                                                    <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                                                        <div className="min-w-0">
                                                            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-emerald-400/80">Top gainers</div>
                                                            <div className="space-y-2">
                                                                {topMovers.gainers.map((mover) => (
                                                                    <div key={`gainer-${mover.symbol}`} className="flex min-w-0 items-center justify-between gap-3 rounded-xl bg-white/[0.03] px-3 py-2 text-sm">
                                                                        <div className="min-w-0"><div className="truncate font-semibold text-slate-100">{mover.symbol}</div><div className="truncate text-xs text-slate-500">{mover.name}</div></div>
                                                                        <div className="flex-shrink-0 text-right"><div className="font-semibold text-emerald-400">+{mover.dailyPct.toFixed(2)}%</div><div className="text-xs text-emerald-400/80">+{currencySymbol}{Math.abs(mover.dailyPnL).toFixed(2)}</div></div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <div className="mb-3 text-xs font-semibold uppercase tracking-wider text-rose-400/80">Top losers</div>
                                                            <div className="space-y-2">
                                                                {topMovers.losers.map((mover) => (
                                                                    <div key={`loser-${mover.symbol}`} className="flex min-w-0 items-center justify-between gap-3 rounded-xl bg-white/[0.03] px-3 py-2 text-sm">
                                                                        <div className="min-w-0"><div className="truncate font-semibold text-slate-100">{mover.symbol}</div><div className="truncate text-xs text-slate-500">{mover.name}</div></div>
                                                                        <div className="flex-shrink-0 text-right"><div className="font-semibold text-rose-400">{mover.dailyPct.toFixed(2)}%</div><div className="text-xs text-rose-400/80">-{currencySymbol}{Math.abs(mover.dailyPnL).toFixed(2)}</div></div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="rounded-2xl border border-white/5 bg-[#1e1e26] p-6 shadow-lg md:col-span-2">
                                                <div className="mb-5 flex items-center justify-between gap-3">
                                                    <div>
                                                        <h4 className="font-bold text-slate-100">Portfolio snapshot</h4>
                                                        <p className="mt-1 text-xs text-slate-500">Largest, best, worst and income leader.</p>
                                                    </div>
                                                    <button onClick={() => setView('holdings')} className="rounded-xl bg-sky-600 px-3 py-2 text-xs font-bold text-white hover:bg-sky-500">View full portfolio</button>
                                                </div>
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                    <div className="rounded-xl bg-white/[0.03] p-3"><div className="text-xs text-slate-500">Largest holding</div><div className="mt-1 truncate font-semibold text-white">{portfolioSnapshot.largestHolding?.symbol || '—'}</div><div className="truncate text-xs text-slate-400">{portfolioSnapshot.largestHolding?.name || 'No holdings yet'}</div></div>
                                                    <div className="rounded-xl bg-white/[0.03] p-3"><div className="text-xs text-slate-500">Best mover today</div><div className="mt-1 truncate font-semibold text-emerald-400">{portfolioSnapshot.bestMover ? `${portfolioSnapshot.bestMover.symbol} +${currencySymbol}${Math.abs(portfolioSnapshot.bestMover.dailyPnL).toFixed(2)}` : '—'}</div><div className="truncate text-xs text-slate-400">{portfolioSnapshot.bestMover?.name || 'Waiting on quotes'}</div></div>
                                                    <div className="rounded-xl bg-white/[0.03] p-3"><div className="text-xs text-slate-500">Worst mover today</div><div className="mt-1 truncate font-semibold text-rose-400">{portfolioSnapshot.worstMover ? `${portfolioSnapshot.worstMover.symbol} -${currencySymbol}${Math.abs(portfolioSnapshot.worstMover.dailyPnL).toFixed(2)}` : '—'}</div><div className="truncate text-xs text-slate-400">{portfolioSnapshot.worstMover?.name || 'Waiting on quotes'}</div></div>
                                                    <div className="rounded-xl bg-white/[0.03] p-3"><div className="text-xs text-slate-500">Highest dividend received</div><div className="mt-1 truncate font-semibold text-white">{portfolioSnapshot.highestDividendReceived?.symbol || '—'}</div><div className="truncate text-xs text-slate-400">{portfolioSnapshot.highestDividendReceived ? `${currencySymbol}${(portfolioSnapshot.highestDividendReceived.totalDividends || portfolioSnapshot.highestDividendReceived.historicDividendsReceived || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'No dividends recorded'}</div></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="min-w-0 rounded-2xl border border-white/5 bg-[#1e1e26] p-6 shadow-lg">
                                            <h4 className="mb-2 font-bold text-slate-100">Allocation</h4>
                                            <p className="mb-4 text-xs text-slate-500">0% hidden; sub-1% positions grouped as Other.</p>
                                            <AllocationChart assets={assets} />
                                        </div>
                                    </div>
                                </section>

                                <section className="space-y-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-white">Income</h3>
                                        <p className="text-sm text-slate-400">Received dividends, future payments and recent events.</p>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
                                        <div className="min-w-0 rounded-2xl border border-white/5 bg-[#1e1e26] p-6 shadow-lg xl:col-span-2">
                                            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                <div>
                                                    <h4 className="font-bold text-slate-100">Dividends received</h4>
                                                    <p className="mt-1 text-xs text-slate-500">Actual recorded income history.</p>
                                                </div>
                                                <div className="text-left sm:text-right">
                                                    <div className="text-2xl font-bold text-white">{currencySymbol}{totalDividendsReceived.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                                    <div className="text-xs text-slate-500">Total received</div>
                                                </div>
                                            </div>
                                            <DividendChart data={monthlyDividends} color="#38BDF8" currency={currencySymbol} />
                                        </div>
                                        <FutureDividends monthlyForecast={dividendForecast.monthlyForecast} next12mTotal={dividendForecast.next12mTotal} monthlyAverage={dividendForecast.monthlyAverage} upcomingEvents={dividendForecast.upcomingEvents} currencySymbol={currencySymbol} horizonMonths={futureDividendHorizonMonths} onHorizonChange={setFutureDividendHorizonMonths} />
                                        <div className="xl:col-span-3">
                                            <DividendPayoutConfirmations payouts={pendingDividendPayoutsForAll} onConfirm={handleConfirmDividendPayout} />
                                            <DividendEvents events={dividendForecast.upcomingEvents} currencySymbol={currencySymbol} horizonDays={dividendHorizonDays} onHorizonChange={setDividendHorizonDays} />
                                        </div>
                                    </div>
                                </section>
                            </div>
                        )}

                        {view === 'holdings' && (
                            selectedAsset ? (
                                <AssetDetail
                                    asset={selectedAsset}
                                    startEditing={startEditingHolding}
                                    uid={isGuest ? null : user?.uid}
                                    onBack={() => {
                                        setSelectedAsset(null);
                                        setStartEditingHolding(false);
                                    }}
                                    onSaveOverride={saveOverride}
                                    onQuoteUpdate={applyQuote}
                                    transactions={currentTransactions}
                                    onUpdateTransaction={handleUpdateTransaction}
                                    onDeleteTransaction={handleDeleteTransaction}
                                />
                            ) : (
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between mt-1 mb-1">
                                        <div>
                                            <h2 className="text-lg font-semibold text-white tracking-tight">Selected portfolio</h2>
                                            <span className="text-xs text-slate-500">Portfolio: {currentPortfolioName}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                                        {selectedPortfolioHeaderCards.map((card) => {
                                            const iconByTitle = {
                                                'Portfolio value': <Wallet size={18} />,
                                                'Total return': <LineChart size={18} />,
                                                IRR: <Activity size={18} />,
                                                'Dividend income': <HandCoins size={18} />,
                                            }[card.title];
                                            const accentByTitle = {
                                                'Portfolio value': '#60a5fa',
                                                'Total return': '#22c55e',
                                                IRR: '#ec4899',
                                                'Dividend income': '#fbbf24',
                                            }[card.title];
                                            const trendByTitle = card.title === 'Total return' ? (portfolioTotalProfit >= 0 ? 'up' : 'down') : undefined;

                                            return (
                                                <MetricCard
                                                    key={card.title}
                                                    title={card.title}
                                                    value={card.value}
                                                    subValue={card.subValue}
                                                    secondaryValue={card.secondaryValue}
                                                    icon={iconByTitle}
                                                    trend={trendByTitle}
                                                    tooltip={card.tooltip}
                                                    accentColor={accentByTitle}
                                                />
                                            );
                                        })}
                                    </div>

                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                        <div className="lg:col-span-2 space-y-8">
                                            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                                                <div className="flex justify-between items-center mb-6">
                                                    <h3 className="font-bold text-slate-200 flex items-center">
                                                        <CalendarRange size={16} className="mr-2 text-sky-400" />
                                                        Dividends Received
                                                    </h3>
                                                    <div className="text-right">
                                                        <div className="text-xl font-bold text-white">
                                                            {currencySymbol}
                                                            {portfolioDividendsReceived.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                        </div>
                                                        <div className="text-xs text-slate-500">Total received</div>
                                                    </div>
                                                </div>
                                                <div className="h-64 w-full">
                                                    <DividendChart data={portfolioMonthlyDividends} color="#8b5cf6" currency={currencySymbol} />
                                                </div>
                                            </div>

                                            <DividendPayoutConfirmations
                                                payouts={pendingDividendPayoutsForPortfolioDisplay}
                                                onConfirm={handleConfirmDividendPayout}
                                            />

                                            <DividendEvents
                                                events={currentDividendForecast.upcomingEvents}
                                                currencySymbol={currencySymbol}
                                                horizonDays={dividendHorizonDays}
                                                onHorizonChange={setDividendHorizonDays}
                                            />
                                        </div>

                                        <div className="space-y-8">
                                            <FutureDividends
                                                monthlyForecast={currentDividendForecast.monthlyForecast}
                                                next12mTotal={currentDividendForecast.next12mTotal}
                                                monthlyAverage={currentDividendForecast.monthlyAverage}
                                                upcomingEvents={currentDividendForecast.upcomingEvents}
                                                currencySymbol={currencySymbol}
                                                horizonMonths={futureDividendHorizonMonths}
                                                onHorizonChange={setFutureDividendHorizonMonths}
                                            />

                                            <div className="bg-[#1e1e26] p-6 rounded-2xl border border-white/5 shadow-lg">
                                                <h3 className="font-bold text-slate-200 mb-6">Allocation</h3>
                                                <AllocationChart assets={portfolioAssets} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-[#1e1e26] rounded-2xl border border-white/5 shadow-lg overflow-hidden">
                                        <div className="p-4 border-b border-white/5 flex items-center justify-between bg-[#1e1e26]">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <div className="flex items-center gap-1 p-1 bg-[#13131a] border border-white/10 rounded-xl text-sm text-slate-400 shadow-sm">
                                                    <button
                                                        onClick={() => setHoldingsCurrencyMode('holding')}
                                                        className={`px-3 py-1.5 rounded-lg transition-colors ${
                                                            holdingsCurrencyMode === 'holding'
                                                                ? 'bg-white text-slate-900 font-semibold'
                                                                : 'text-slate-400 hover:text-white'
                                                        }`}
                                                    >
                                                        In holding currency
                                                    </button>
                                                    <button
                                                        onClick={() => setHoldingsCurrencyMode('preferred')}
                                                        className={`px-3 py-1.5 rounded-lg transition-colors ${
                                                            holdingsCurrencyMode === 'preferred'
                                                                ? 'bg-white text-slate-900 font-semibold'
                                                                : 'text-slate-400 hover:text-white'
                                                        }`}
                                                    >
                                                        In {selectedCurrency}
                                                    </button>
                                                </div>
                                                <div className="flex items-center space-x-2 px-3 py-2 bg-[#13131a] border border-white/10 rounded-lg text-sm text-slate-400 shadow-sm">
                                                    <Search size={16} />
                                                    <input
                                                        type="text"
                                                        value={portfolioSearch}
                                                        onChange={(event) => setPortfolioSearch(event.target.value)}
                                                        placeholder="Search by symbol or name"
                                                        className="bg-transparent text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none w-48"
                                                        autoComplete="off"
                                                        spellCheck={false}
                                                    />
                                                </div>
                                                <div className="h-4 w-px bg-white/10 mx-2"></div>
                                                <span className="text-sm text-slate-500">
                                                    {normalizedPortfolioSearch
                                                        ? `${filteredPortfolioAssets.length} of ${portfolioAssets.length} Assets found`
                                                        : `${portfolioAssets.length} Assets found`}
                                                </span>
                                                <div className="h-4 w-px bg-white/10 mx-2"></div>
                                                <div className="flex items-center gap-3">
                                                    <select
                                                        value={holdingsPageSize}
                                                        onChange={(event) => {
                                                            setHoldingsPageSize(Number(event.target.value));
                                                            setHoldingsPage(0);
                                                        }}
                                                        className="px-3 py-2 rounded-lg bg-[#13131a] border border-white/10 text-sm text-slate-200 focus:outline-none"
                                                        aria-label="Holdings to show"
                                                    >
                                                        <option value={10}>10</option>
                                                        <option value={25}>25</option>
                                                        <option value={50}>50</option>
                                                    </select>
                                                    <span className="text-sm text-slate-400">
                                                        See {visibleHoldingsStart}-{visibleHoldingsEnd} from {filteredPortfolioAssets.length}
                                                    </span>
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() => setHoldingsPage((page) => Math.max(page - 1, 0))}
                                                            disabled={holdingsPage === 0}
                                                            className="p-1.5 rounded-md border border-white/10 text-slate-300 disabled:text-slate-600 disabled:border-white/5 disabled:cursor-not-allowed hover:text-white hover:border-white/20 transition-colors"
                                                            aria-label="Previous holdings page"
                                                        >
                                                            <ChevronLeft size={16} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setHoldingsPage((page) => Math.min(page + 1, totalHoldingsPages - 1))}
                                                            disabled={holdingsPage >= totalHoldingsPages - 1}
                                                            className="p-1.5 rounded-md border border-white/10 text-slate-300 disabled:text-slate-600 disabled:border-white/5 disabled:cursor-not-allowed hover:text-white hover:border-white/20 transition-colors"
                                                            aria-label="Next holdings page"
                                                        >
                                                            <ChevronRight size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button className="text-slate-500 hover:text-white">
                                                    <Download size={18} />
                                                </button>
                                            </div>
                                        </div>
                                        <PortfolioTable
                                            assets={displayedPortfolioAssets}
                                            selectedCurrency={selectedCurrency}
                                            displayCurrencyMode={holdingsCurrencyMode}
                                            liveQuotes={liveQuotes}
                                            onSelectAsset={handleSelectAsset}
                                            onResolveInstrument={handleResolveUnresolvedInstrument}
                                        />
                                    </div>

                                </div>
                            )
                        )}

                        {view === 'ai-lab' && (
                            <div className="max-w-5xl mx-auto animate-in fade-in zoom-in duration-500 space-y-6">
                                <div className={`rounded-3xl p-8 shadow-xl border ${theme === 'light' ? 'bg-white border-slate-200' : 'bg-[#11131f] border-white/10'}`}>
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <h2 className={`text-2xl font-bold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>AI Portfolio Analyst</h2>
                                            <p className={theme === 'light' ? 'text-sm text-sky-700' : 'text-sm text-sky-200'}>Powered by AI</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleLoadAnalystSnapshot}
                                            disabled={isLoadingAnalystSnapshot}
                                            className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 px-6 py-2 text-sm font-semibold text-white shadow-lg shadow-sky-600/30 transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isLoadingAnalystSnapshot ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                                            {isLoadingAnalystSnapshot ? 'Analyzing...' : analystSnapshot || portfolioInsight ? 'Refresh Report' : 'Generate Report'}
                                        </button>
                                    </div>

                                    {!analystSnapshot && !portfolioInsight && !isLoadingAnalystSnapshot && (
                                        <div className={`mt-6 rounded-2xl border-2 border-dashed px-6 py-10 text-center ${theme === 'light' ? 'border-slate-300 bg-slate-50 text-slate-700' : 'border-slate-700 bg-slate-900/30 text-slate-300'}`}>
                                            <div className="text-3xl">📊</div>
                                            <p className="mt-3 text-lg font-medium">Ready to generate your unified AI report</p>
                                            <p className="mt-2 text-sm opacity-80">AI will combine portfolio health diagnostics with FIRE progress insights in one place.</p>
                                        </div>
                                    )}

                                    {isLoadingAnalystSnapshot && (
                                        <div className="mt-6 space-y-3">
                                            <div className={`h-24 rounded-2xl animate-pulse ${theme === 'light' ? 'bg-slate-200' : 'bg-[#1e1e26]'}`} />
                                            <div className={`h-48 rounded-2xl animate-pulse ${theme === 'light' ? 'bg-slate-200' : 'bg-[#1e1e26]'}`} />
                                        </div>
                                    )}
                                    {analystSnapshotError && (
                                        <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                                            {analystSnapshotError}
                                        </div>
                                    )}
                                    {portfolioInsightError && (
                                        <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                                            {portfolioInsightError}
                                        </div>
                                    )}

                                    {!isLoadingAnalystSnapshot && (analystSnapshot || portfolioInsight) && (
                                        <div className="mt-6 space-y-6">
                                            {portfolioInsight && (
                                                <div className={`rounded-2xl border p-6 ${theme === 'light' ? 'border-slate-200 bg-slate-50 text-slate-900' : 'border-white/10 bg-[#1e1e26] text-white'}`}>
                                                    <h3 className={`text-xl font-semibold ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>Portfolio Health Score</h3>
                                                    <div
                                                        className={`mt-4 max-w-none ${theme === 'light' ? 'prose prose-slate prose-p:text-slate-700 prose-headings:text-slate-900 prose-li:text-slate-700' : 'prose prose-invert prose-p:text-slate-300 prose-headings:text-white prose-li:text-slate-300'}`}
                                                        dangerouslySetInnerHTML={{ __html: portfolioInsight }}
                                                    />
                                                </div>
                                            )}
                                            {analystSnapshot && <AnalystSnapshot snapshot={analystSnapshot} />}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {view === 'fire' && (
                            <div className="max-w-6xl mx-auto animate-in fade-in zoom-in duration-500">
                                <FireSnapshot
                                    portfolioValue={totalValue}
                                    portfolioCashValue={portfolioCashValue}
                                    passiveIncome={projectedPortfolioDividend}
                                    currencySymbol={currencySymbol}
                                    config={fireConfig}
                                    onUpdateConfig={handleUpdateFireConfig}
                                />
                            </div>
                        )}

                        {view === 'pensions' && (
                            <div className="max-w-6xl mx-auto animate-in fade-in zoom-in duration-500">
                                <PensionDashboard
                                    config={fireConfig}
                                    liquidAssets={totalValue}
                                    currencySymbol={currencySymbol}
                                />
                            </div>
                        )}

                        {view === 'analytics' && (
                            <div className="text-center py-20">
                                <div className="w-24 h-24 bg-[#1e1e26] border border-white/5 rounded-full flex items-center justify-center mx-auto mb-6 shadow-xl">
                                    <LineChart size={40} className="text-sky-500" />
                                </div>
                                <h2 className="text-2xl font-bold text-white mb-2">Advanced Analytics</h2>
                                <p className="text-slate-500 max-w-md mx-auto">
                                    Deep dive into Monte Carlo simulations, Sharpe ratios, and benchmark comparisons. This module is currently under development.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <PortfolioUploadModal
                open={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onImport={handleSmartImport}
                defaultCurrency={selectedCurrency}
            />
            <TransactionModal
                open={showTransactionModal}
                onClose={() => {
                    setShowTransactionModal(false);
                    setTransactionModalDraft(null);
                }}
                onSave={handleManualTransactionSave}
                defaultCurrency={selectedCurrency}
                suggestions={transactionSuggestions}
                initialDraft={transactionModalDraft}
            />
            {showScrollTop && (
                <button
                    type="button"
                    aria-label="Scroll to top"
                    className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-sky-500 text-white shadow-lg transition hover:bg-sky-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <ArrowUp size={20} />
                </button>
            )}
            {holdingDeleteCandidate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
                    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#111118] shadow-2xl">
                        <div className="border-b border-white/5 px-6 py-4">
                            <h3 className="text-lg font-semibold text-white">Delete transaction?</h3>
                        </div>
                        <div className="px-6 py-4 text-sm text-slate-400">
                            This will remove all transactions for{' '}
                            <span className="font-semibold text-slate-200">
                                {holdingDeleteCandidate.symbol.toUpperCase()}
                            </span>{' '}
                            and recalculate holdings.
                        </div>
                        <div className="flex items-center justify-end gap-2 border-t border-white/5 px-6 py-4">
                            <button
                                className="rounded-lg border border-white/10 px-4 py-2 text-sm text-slate-300 hover:bg-white/5"
                                onClick={() => setHoldingDeleteCandidate(null)}
                            >
                                Cancel
                            </button>
                            <button
                                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-500"
                                onClick={() => {
                                    handleDeleteHolding(holdingDeleteCandidate);
                                    setHoldingDeleteCandidate(null);
                                }}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default App;

